import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import salesAgentService, { LeadCaptureState, SalesAgent, SalesAgentUpdate, TerminologyBank, TerminologyEntry, VoiceSettings } from '@/lib/api/salesAgent';
import cartesiaService, { CartesiaVoice } from '@/lib/api/cartesia';
import organizationsService, { PronunciationLibraryEntry } from '@/lib/api/organizations';
import promptVersionsService from '@/lib/api/promptVersions';
import type { PromptVersion } from '@/lib/api/promptVersions';
import PromptDiffView from '@/components/prompt-diff/PromptDiffView';
import apiClient from '@/lib/api/client';
import type { FileUploadResponse } from '@/lib/api/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TooltipLabel } from '@/components/ui/TooltipLabel';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ColorPicker,
  ColorPickerEyeDropper,
  ColorPickerHexInput,
  ColorPickerHue,
  ColorPickerSelection,
} from '@/components/ui/color-picker';
import { Loader2, Save, Sparkles, FileText, Edit, Image, Upload, X, ChevronLeft, ChevronRight, AudioLines, Play, Pause, Search, Globe2, MousePointerClick, Plus, Trash2, Languages } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { googleFonts } from '@/constants/googleFonts';

const LAUNCHER_PREVIEW_ICON_URL = 'https://storage.googleapis.com/hobbes-assets/Favicon.svg';
const DEFAULT_LAUNCHER_TINT = '#14120E';
const DEFAULT_LAUNCHER_ICON_BG = 'rgba(20,18,14,0.88)';

type LauncherPreviewMode =
  | 'compact'
  | 'rest'
  | 'composed'
  | 'active'
  | 'embeddedEntry'
  | 'embeddedActive'
  | 'fullscreenEntry'
  | 'fullscreenActive';
type ExperiencePreviewFamily = 'launcher' | 'embedded' | 'fullscreen';
type LauncherPreviewSurface = 'dark' | 'light';
type PreviewDevice = 'desktop' | 'mobile';
const BRANDING_ROW_CLASS = 'md:grid-cols-[120px_minmax(0,1fr)]';

type AgentExperiencePreviewMode =
  | 'launcher-icon'
  | 'launcher-rest'
  | 'launcher-open'
  | 'launcher-active'
  | 'embedded-entry'
  | 'embedded-active'
  | 'fullscreen-entry'
  | 'fullscreen-active';

type HobbesAgentPreviewProps = {
  mode: AgentExperiencePreviewMode;
  surface?: 'dark' | 'light';
  backgroundScreenshotUrl?: string | null;
  headerLogoUrl?: string | null;
  entryModalLogoUrl?: string | null;
  launcherIconUrl?: string | null;
  launcherIconBgColor?: string | null;
  launcherText?: string | null;
  launcherFontFamily?: string | null;
  launcherTextColor?: string | null;
  launcherThumbnailUrl?: string | null;
  leadCaptureEmail?: LeadCaptureState | null;
  primaryColor?: string | null;
  companyName?: string | null;
  isMobilePreview?: boolean;
};

type HobbesAgentPreviewHandle = {
  update: (props: HobbesAgentPreviewProps) => void;
  destroy: () => void;
};

type HobbesAgentPreviewApi = {
  mount: (container: HTMLElement, props: HobbesAgentPreviewProps) => HobbesAgentPreviewHandle;
};

declare global {
  interface Window {
    HobbesAgentPreview?: HobbesAgentPreviewApi;
  }
}

let previewScriptPromise: Promise<void> | null = null;
let previewScriptPromiseUrl: string | null = null;
let localPreviewRuntimeReloaded = false;

function getHobbesAgentPreviewUrl() {
  const explicitUrl = import.meta.env.VITE_HOBBES_AGENT_PREVIEW_URL as string | undefined;
  if (explicitUrl?.trim()) {
    return explicitUrl.trim();
  }

  if (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return 'http://localhost:3334/preview.js';
  }

  return 'https://agent.hihobbes.com/preview.js';
}

function loadHobbesAgentPreviewScript(src: string) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Preview runtime is only available in the browser.'));
  }

  const shouldReloadLocalRuntime = (() => {
    if (localPreviewRuntimeReloaded) return false;
    if (!['localhost', '127.0.0.1'].includes(window.location.hostname)) return false;

    try {
      const previewUrl = new URL(src, window.location.href);
      return ['localhost', '127.0.0.1'].includes(previewUrl.hostname);
    } catch {
      return false;
    }
  })();

  if (shouldReloadLocalRuntime) {
    localPreviewRuntimeReloaded = true;
    previewScriptPromise = null;
    previewScriptPromiseUrl = null;
    window.HobbesAgentPreview = undefined;
    document
      .querySelectorAll<HTMLScriptElement>('script[data-hobbes-agent-preview-script]')
      .forEach((script) => script.remove());
  }

  if (window.HobbesAgentPreview) {
    return Promise.resolve();
  }

  if (previewScriptPromise && previewScriptPromiseUrl === src) {
    return previewScriptPromise;
  }

  previewScriptPromiseUrl = src;
  previewScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = Array.from(
      document.querySelectorAll<HTMLScriptElement>('script[data-hobbes-agent-preview-script]'),
    ).find((script) => script.src === src);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load preview runtime.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.setAttribute('data-hobbes-agent-preview-script', 'true');
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => {
      previewScriptPromise = null;
      previewScriptPromiseUrl = null;
      reject(new Error('Unable to load preview runtime.'));
    }, { once: true });
    document.head.appendChild(script);
  });

  return previewScriptPromise;
}

function normalizeHexColor(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  const hex = match[1];
  if (hex.length === 3) {
    return `#${hex.split('').map((char) => `${char}${char}`).join('')}`.toUpperCase();
  }
  return `#${hex}`.toUpperCase();
}

function LauncherTintPicker({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (value: string) => void;
}) {
  const normalizedTint = normalizeHexColor(value);
  const pickerValue = normalizedTint || DEFAULT_LAUNCHER_TINT;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-9 w-full justify-start gap-2 rounded-lg px-3">
          <span
            aria-hidden
            className="h-4 w-4 shrink-0 rounded-full border border-border"
            style={{ backgroundColor: pickerValue }}
          />
          <span className="truncate text-sm font-normal">
            {normalizedTint || 'Default'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-lg p-3">
        <ColorPicker
          value={pickerValue}
          defaultValue={DEFAULT_LAUNCHER_TINT}
          onChange={(hex) => onChange(hex)}
          className="bg-transparent p-0"
        >
          <ColorPickerSelection className="h-36" />
          <ColorPickerHue />
          <div className="flex items-center gap-2">
            <ColorPickerHexInput />
            <ColorPickerEyeDropper />
          </div>
        </ColorPicker>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2 h-8 w-full rounded-lg text-muted-foreground"
          onClick={() => onChange('')}
        >
          Clear tint
        </Button>
      </PopoverContent>
    </Popover>
  );
}

// Preview adapter only: the visuals themselves live in the bundled widget
// preview runtime so Platform does not maintain a second launcher implementation.
function StickyTriggerMorphPreview({
  iconUrl,
  text,
  tintColor,
  textColor,
  fontFamily,
  thumbnailUrl,
  leadCaptureEmail,
  iconBgColor,
  activeScreenshotUrl,
  headerLogoUrl,
  entryModalLogoUrl,
  companyName,
  previewMode,
  previewSurface,
  isMobilePreview = false,
}: {
  iconUrl: string;
  text: string;
  tintColor: string | null;
  textColor: string | null;
  fontFamily: string | null;
  thumbnailUrl: string;
  leadCaptureEmail: LeadCaptureState;
  iconBgColor?: string | null;
  activeScreenshotUrl?: string | null;
  headerLogoUrl?: string | null;
  entryModalLogoUrl?: string | null;
  companyName?: string | null;
  previewMode?: LauncherPreviewMode;
  previewSurface?: LauncherPreviewSurface;
  isMobilePreview?: boolean;
}) {
  const previewContainerRef = React.useRef<HTMLDivElement | null>(null);
  const previewHandleRef = React.useRef<HobbesAgentPreviewHandle | null>(null);
  const [previewStatus, setPreviewStatus] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const activePreviewMode = previewMode ?? 'composed';
  const activePreviewSurface = previewSurface ?? 'dark';
  const resolvedIconUrl = iconUrl || LAUNCHER_PREVIEW_ICON_URL;
  const label = text || 'Start Demo';
  const normalizedTint = normalizeHexColor(tintColor);
  const normalizedIconBg = normalizeHexColor(iconBgColor);
  const normalizedTextColor = normalizeHexColor(textColor);
  const widgetPreviewMode = activePreviewMode === 'compact'
    ? 'launcher-icon'
    : activePreviewMode === 'rest'
      ? 'launcher-rest'
      : activePreviewMode === 'composed'
        ? 'launcher-open'
        : activePreviewMode === 'embeddedEntry'
          ? 'embedded-entry'
          : activePreviewMode === 'embeddedActive'
            ? 'embedded-active'
            : activePreviewMode === 'fullscreenEntry'
              ? 'fullscreen-entry'
              : activePreviewMode === 'fullscreenActive'
                ? 'fullscreen-active'
                : 'launcher-active';
  const isFullscreenPreview = widgetPreviewMode === 'fullscreen-entry' || widgetPreviewMode === 'fullscreen-active';
  const previewProps = React.useMemo<HobbesAgentPreviewProps>(() => {
    const nextProps: HobbesAgentPreviewProps = {
      mode: widgetPreviewMode,
      backgroundScreenshotUrl: activeScreenshotUrl,
      headerLogoUrl,
      entryModalLogoUrl,
      launcherIconUrl: resolvedIconUrl,
      launcherIconBgColor: normalizedIconBg || null,
      launcherText: label,
      launcherFontFamily: fontFamily,
      launcherTextColor: normalizedTextColor,
      launcherThumbnailUrl: thumbnailUrl || null,
      leadCaptureEmail,
      primaryColor: normalizedTint || undefined,
      companyName,
      isMobilePreview,
    };

    if (!isFullscreenPreview) {
      nextProps.surface = activePreviewSurface;
    }

    return nextProps;
  }, [
    activePreviewSurface,
    activeScreenshotUrl,
    companyName,
    entryModalLogoUrl,
    fontFamily,
    headerLogoUrl,
    isFullscreenPreview,
    isMobilePreview,
    label,
    leadCaptureEmail,
    normalizedIconBg,
    normalizedTextColor,
    normalizedTint,
    resolvedIconUrl,
    thumbnailUrl,
    widgetPreviewMode,
  ]);

  React.useEffect(() => {
    let isActive = true;
    const previewUrl = getHobbesAgentPreviewUrl();

    setPreviewStatus(window.HobbesAgentPreview ? 'ready' : 'loading');

    loadHobbesAgentPreviewScript(previewUrl)
      .then(() => {
        if (!isActive || !previewContainerRef.current || !window.HobbesAgentPreview) {
          return;
        }

        if (previewHandleRef.current) {
          previewHandleRef.current.update(previewProps);
        } else {
          previewHandleRef.current = window.HobbesAgentPreview.mount(
            previewContainerRef.current,
            previewProps,
          );
        }

        setPreviewStatus('ready');
      })
      .catch((error) => {
        console.warn('[HobbesPlatform] Failed to load agent preview runtime', error);
        if (isActive) {
          setPreviewStatus('error');
        }
      });

    return () => {
      isActive = false;
    };
  }, [previewProps]);

  React.useEffect(() => () => {
    previewHandleRef.current?.destroy();
    previewHandleRef.current = null;
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <div ref={previewContainerRef} className="h-full w-full" />
      {previewStatus === 'loading' && (
        <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-[#0b0f14] text-xs text-white/60">
          Loading preview...
        </div>
      )}
      {previewStatus === 'error' && (
        <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center bg-[#0b0f14] px-4 text-center">
          <p className="text-sm font-medium text-white">Preview unavailable</p>
          <p className="mt-1 max-w-xs text-xs text-white/55">
            Start the bundled widget preview with <span className="font-mono">npm run serve:widget</span>.
          </p>
        </div>
      )}
    </div>
  );
}

const CONVERSATION_CONTROL_MODE_OPTIONS = [
  {
    value: 'exploratory',
    label: 'Exploratory',
    description: 'Lets the visitor guide more of the path with lighter direction.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Leads when the next step is obvious, but still pauses at real branch points.',
  },
  {
    value: 'directive',
    label: 'Directive',
    description: 'Drives the strongest next proof point proactively with minimal permission-seeking.',
  },
] as const;

const getConversationControlModeOption = (value?: string | null) =>
  CONVERSATION_CONTROL_MODE_OPTIONS.find((option) => option.value === value)
  || CONVERSATION_CONTROL_MODE_OPTIONS.find((option) => option.value === 'balanced')!;

type ConfigSection = 'general' | 'branding' | 'conversation' | 'collateralAccess';

const CONFIG_SECTIONS: {
  key: ConfigSection;
  icon: React.FC<{ className?: string }>;
  label: string;
  description: string;
}[] = [
  { key: 'general', icon: Sparkles, label: 'General', description: 'Manage agent availability and follow-up destinations.' },
  { key: 'branding', icon: MousePointerClick, label: 'Branding', description: 'Customize the customer-facing agent entry experience.' },
  { key: 'conversation', icon: AudioLines, label: 'Conversation', description: 'Tune how the agent speaks, guides visitors, and suggests follow-ups.' },
  { key: 'collateralAccess', icon: FileText, label: 'Collateral Access', description: 'Choose which sales and product materials the agent can reference.' },
];

const TRANSPARENCY_GRID_STYLE: React.CSSProperties = {
  backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)',
  backgroundSize: '16px 16px',
};

function ConfigFieldLabel({
  label,
  description,
  htmlFor,
  className,
}: {
  label: React.ReactNode;
  description: React.ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  const labelNode = (
    <TooltipLabel
      label={label}
      description={description}
      className={cn('text-sm font-medium text-heading', className)}
    />
  );

  if (!htmlFor) {
    return labelNode;
  }

  return (
    <Label htmlFor={htmlFor} className="inline-flex cursor-inherit">
      {labelNode}
    </Label>
  );
}

const SettingsLabel = ConfigFieldLabel;

function ConfigSurface({
  children,
  className,
  divided = true,
}: {
  children: React.ReactNode;
  className?: string;
  divided?: boolean;
}) {
  return (
    <section
      className={cn(
        'max-w-4xl overflow-hidden rounded-lg border border-border/80 bg-background/60',
        divided && 'divide-y divide-border/60',
        className,
      )}
    >
      {children}
    </section>
  );
}

function ConfigGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h2 className="px-1 text-xs font-medium text-muted-foreground">{title}</h2>
      {children}
    </div>
  );
}

function ConfigRow({
  label,
  description,
  htmlFor,
  children,
  action,
  className,
  contentClassName,
}: {
  label: React.ReactNode;
  description: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <div
      className={cn(
        'grid gap-3 px-4 py-4 md:items-center',
        action ? 'md:grid-cols-[200px_minmax(0,1fr)_auto]' : 'md:grid-cols-[200px_minmax(0,1fr)]',
        className,
      )}
    >
      <div>
        <ConfigFieldLabel label={label} description={description} htmlFor={htmlFor} />
      </div>
      <div className={cn('min-w-0 text-sm text-body', contentClassName)}>
        {children}
      </div>
      {action && <div className="flex items-center md:justify-self-end">{action}</div>}
    </div>
  );
}

function ConfigValue({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-lg border border-border/70 bg-background px-3 py-2 text-sm text-heading', className)}>
      {children}
    </div>
  );
}

function ConfigPreviewSlot({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative flex min-h-[140px] items-center justify-center rounded-lg border border-border/70 bg-background p-4', className)}>
      {children}
    </div>
  );
}

function ConfigEmptyState({
  icon,
  title,
}: {
  icon?: React.ReactNode;
  title: string;
}) {
  return (
    <div className="text-center text-muted-foreground">
      {icon}
      <p className="mt-3 text-sm font-medium text-heading">{title}</p>
    </div>
  );
}

function ExperiencePreviewPanel({
  iconUrl,
  text,
  tintColor,
  textColor,
  fontFamily,
  thumbnailUrl,
  leadCaptureEmail,
  iconBgColor,
  activeScreenshotUrl,
  headerLogoUrl,
  entryModalLogoUrl,
  companyName,
}: {
  iconUrl: string;
  text: string;
  tintColor: string | null;
  textColor: string | null;
  fontFamily: string | null;
  thumbnailUrl: string;
  leadCaptureEmail: LeadCaptureState;
  iconBgColor?: string | null;
  activeScreenshotUrl?: string | null;
  headerLogoUrl?: string | null;
  entryModalLogoUrl?: string | null;
  companyName?: string | null;
}) {
  const viewportIsMobile = useIsMobile();
  const [previewFamily, setPreviewFamily] = React.useState<ExperiencePreviewFamily>('launcher');
  const [launcherPreviewMode, setLauncherPreviewMode] = React.useState<LauncherPreviewMode>('composed');
  const [embeddedPreviewMode, setEmbeddedPreviewMode] = React.useState<LauncherPreviewMode>('embeddedEntry');
  const [fullscreenPreviewMode, setFullscreenPreviewMode] = React.useState<LauncherPreviewMode>('fullscreenEntry');
  const [previewSurface, setPreviewSurface] = React.useState<LauncherPreviewSurface>('dark');
  const [previewDevice, setPreviewDevice] = React.useState<PreviewDevice>('desktop');
  const [hasSelectedPreviewDevice, setHasSelectedPreviewDevice] = React.useState(false);
  const previewMode = previewFamily === 'launcher'
    ? launcherPreviewMode
    : previewFamily === 'embedded'
      ? embeddedPreviewMode
      : fullscreenPreviewMode;
  const isMobilePreview = previewDevice === 'mobile';
  const isDarkPreviewSurface = previewSurface === 'dark';
  const showsHostBackgroundControl = previewFamily !== 'fullscreen';

  React.useEffect(() => {
    if (!hasSelectedPreviewDevice) {
      setPreviewDevice(viewportIsMobile ? 'mobile' : 'desktop');
    }
  }, [hasSelectedPreviewDevice, viewportIsMobile]);

  const controlButtonClass = (active: boolean) => cn(
    'rounded px-2.5 py-1.5 text-[11px] font-medium transition-colors',
    active
      ? isDarkPreviewSurface ? 'bg-white text-black shadow-sm' : 'bg-black text-white shadow-sm'
      : isDarkPreviewSurface ? 'text-white/60 hover:text-white' : 'text-black/55 hover:text-black',
  );
  const controlGroupClass = cn(
    'flex rounded-md border p-0.5 shadow-sm backdrop-blur',
    isDarkPreviewSurface ? 'border-white/10 bg-black/25' : 'border-black/10 bg-white/70',
  );

  return (
    <ConfigGroup title="Preview">
      <ConfigSurface divided={false} className="max-w-none">
        <div className="space-y-3 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className={controlGroupClass} aria-label="Preview experience">
                {[
                  { value: 'launcher', label: 'Launcher' },
                  { value: 'embedded', label: 'Embedded' },
                  { value: 'fullscreen', label: 'Full-Screen' },
                ].map((family) => (
                  <button
                    key={family.value}
                    type="button"
                    onClick={() => setPreviewFamily(family.value as ExperiencePreviewFamily)}
                    className={controlButtonClass(previewFamily === family.value)}
                  >
                    {family.label}
                  </button>
                ))}
              </div>
              <div className={controlGroupClass} aria-label="Preview device">
                {[
                  { value: 'desktop', label: 'Desktop' },
                  { value: 'mobile', label: 'Mobile' },
                ].map((device) => (
                  <button
                    key={device.value}
                    type="button"
                    onClick={() => {
                      setHasSelectedPreviewDevice(true);
                      setPreviewDevice(device.value as PreviewDevice);
                    }}
                    className={controlButtonClass(previewDevice === device.value)}
                  >
                    {device.label}
                  </button>
                ))}
              </div>
              {showsHostBackgroundControl && (
                <div className={controlGroupClass} aria-label="Preview host background">
                  {[
                    { value: 'dark', label: 'Dark' },
                    { value: 'light', label: 'Light' },
                  ].map((surface) => (
                    <button
                      key={surface.value}
                      type="button"
                      onClick={() => setPreviewSurface(surface.value as LauncherPreviewSurface)}
                      className={controlButtonClass(previewSurface === surface.value)}
                    >
                      {surface.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={cn(controlGroupClass, 'self-start sm:self-auto')} aria-label="Preview state">
              {(previewFamily === 'launcher'
                ? [
                    { value: 'compact', label: 'Icon' },
                    { value: 'rest', label: 'Rest' },
                    { value: 'composed', label: 'Open' },
                    { value: 'active', label: 'Active' },
                  ]
                : [
                    {
                      value: previewFamily === 'embedded' ? 'embeddedEntry' : 'fullscreenEntry',
                      label: 'Entry',
                    },
                    {
                      value: previewFamily === 'embedded' ? 'embeddedActive' : 'fullscreenActive',
                      label: 'Active',
                    },
                  ]
              ).map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => {
                    if (previewFamily === 'launcher') {
                      setLauncherPreviewMode(mode.value as LauncherPreviewMode);
                    } else if (previewFamily === 'embedded') {
                      setEmbeddedPreviewMode(mode.value as LauncherPreviewMode);
                    } else {
                      setFullscreenPreviewMode(mode.value as LauncherPreviewMode);
                    }
                  }}
                  className={controlButtonClass(previewMode === mode.value)}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
          {fontFamily && (
            <link
              rel="stylesheet"
              href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}&display=swap`}
            />
          )}
          <ConfigPreviewSlot
            className={cn(
              'min-h-0 overflow-hidden p-0',
              isMobilePreview
                ? 'mx-auto aspect-[9/16] w-full max-w-[390px]'
                : 'aspect-video w-full',
            )}
          >
            <StickyTriggerMorphPreview
              iconUrl={iconUrl}
              text={text}
              tintColor={tintColor}
              textColor={textColor}
              fontFamily={fontFamily}
              thumbnailUrl={thumbnailUrl}
              leadCaptureEmail={leadCaptureEmail}
              iconBgColor={iconBgColor}
              activeScreenshotUrl={activeScreenshotUrl}
              headerLogoUrl={headerLogoUrl}
              entryModalLogoUrl={entryModalLogoUrl}
              companyName={companyName}
              previewMode={previewMode}
              previewSurface={showsHostBackgroundControl ? previewSurface : undefined}
              isMobilePreview={isMobilePreview}
            />
          </ConfigPreviewSlot>
        </div>
      </ConfigSurface>
    </ConfigGroup>
  );
}

const createEmptyFormData = (): Partial<SalesAgent> => ({
  name: '',
  is_active: true,
  conversation_control_mode: 'balanced',
  use_active_deck: true,
  use_all_videos: true,
  use_documentation: false,
  contact_email: '',
  calendar_link: '',
  suggestion_instructions: '',
  auto_suggestions_enabled: true,
  entry_modal_logo_url: '',
  header_logo_url: '',
  entry_modal_title: '',
  entry_modal_description: '',
  entry_modal_button_text: '',
  lead_capture_name: 'hidden',
  lead_capture_email: 'required',
  lead_capture_phone: 'hidden',
  lead_capture_business: 'hidden',
  browser_analytics_enabled: true,
  standalone_container_type: 'none',
  standalone_container_id: null,
  background_screenshot_url: '',
  share_title: '',
  share_description: '',
  share_thumbnail_url: '',
  sticky_trigger_icon_url: '',
  sticky_trigger_text: '',
  sticky_trigger_bg_color: '',
  sticky_trigger_text_color: '',
  sticky_trigger_font_family: null,
  sticky_trigger_placement: null,
  sticky_trigger_thumbnail_url: '',
  launcher_icon_bg_color: '',
  voice_settings: null,
  terminology_bank: null,
});

const mapSalesAgentToFormData = (agent: SalesAgent | null | undefined): Partial<SalesAgent> => {
  if (!agent) {
    return createEmptyFormData();
  }

  return {
    ...createEmptyFormData(),
    ...agent,
    voice_settings: agent.voice_settings
      ? {
          ...agent.voice_settings,
        }
      : null,
  };
};

const VOICE_EMOTIONS = [
  { value: 'neutral', label: 'Neutral', description: 'Balanced and steady delivery.' },
  { value: 'content', label: 'Content', description: 'Warm, comfortable, and settled.' },
  { value: 'happy', label: 'Happy', description: 'Positive and upbeat without overdoing it.' },
  { value: 'confident', label: 'Confident', description: 'Assured, polished, and direct.' },
  { value: 'calm', label: 'Calm', description: 'Relaxed and measured pacing.' },
  { value: 'excited', label: 'Excited', description: 'High-energy and animated.' },
  { value: 'enthusiastic', label: 'Enthusiastic', description: 'Engaged and encouraging.' },
  { value: 'curious', label: 'Curious', description: 'Inquisitive and exploratory.' },
  { value: 'determined', label: 'Determined', description: 'Focused and purposeful.' },
  { value: 'surprised', label: 'Surprised', description: 'Expressive with a sense of discovery.' },
  { value: 'proud', label: 'Proud', description: 'Confident with celebratory emphasis.' },
  { value: 'peaceful', label: 'Peaceful', description: 'Soft, composed, and smooth.' },
  { value: 'skeptical', label: 'Skeptical', description: 'Questioning and slightly guarded.' },
  { value: 'sarcastic', label: 'Sarcastic', description: 'Dry and ironic.' },
  { value: 'frustrated', label: 'Frustrated', description: 'Tense with restrained irritation.' },
  { value: 'melancholic', label: 'Melancholic', description: 'Heavy and reflective.' },
  { value: 'anxious', label: 'Anxious', description: 'Uneasy and urgent.' },
  { value: 'sad', label: 'Sad', description: 'Low-energy and subdued.' },
  { value: 'scared', label: 'Scared', description: 'Nervous and apprehensive.' },
  { value: 'angry', label: 'Angry', description: 'Sharp and intense.' },
  { value: 'elated', label: 'Elated', description: 'Very elevated and celebratory.' },
];

const VALID_VOICE_EMOTIONS = new Set([
  'affectionate',
  'agitated',
  'alarmed',
  'amazed',
  'angry',
  'anxious',
  'anticipation',
  'apologetic',
  'bored',
  'calm',
  'confident',
  'confused',
  'contempt',
  'content',
  'contemplative',
  'curious',
  'dejected',
  'determined',
  'disappointed',
  'disgusted',
  'distant',
  'elated',
  'envious',
  'euphoric',
  'happy',
  'hesitant',
  'frustrated',
  'flirtatious',
  'grateful',
  'guilty',
  'hurt',
  'insecure',
  'ironic',
  'joking/comedic',
  'mad',
  'melancholic',
  'mysterious',
  'neutral',
  'nostalgic',
  'outraged',
  'panicked',
  'peaceful',
  'proud',
  'rejected',
  'resigned',
  'sad',
  'sarcastic',
  'scared',
  'skeptical',
  'sympathetic',
  'surprised',
  'serene',
  'threatened',
  'tired',
  'triumphant',
  'trust',
  'wistful',
  'enthusiastic',
  'excited',
]);
const VOICES_PER_PAGE = 8;
const MAX_PRONUNCIATION_ENTRIES = 250;
type VoiceFilter = 'recommended' | 'all' | 'feminine' | 'masculine';
type VoiceEditorPanel = 'voice' | 'style' | 'speed';
const RECOMMENDED_VOICE_KEYWORDS = [
  'brooke',
  'carson',
  'jacqueline',
  'katie',
  'kiefer',
  'ronald',
  'caroline',
  'cathy',
  'blake',
];
const NON_PROFESSIONAL_VOICE_KEYWORDS = [
  'disgust',
  'angry',
  'sarcastic',
  'scared',
  'anxious',
  'melancholic',
  'frustrated',
  'big sister',
  'coworker',
];

const normalizeVoiceName = (name?: string | null) =>
  (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const matchesVoiceGender = (voice: CartesiaVoice, target: 'feminine' | 'masculine') => {
  const normalizedGender = normalizeVoiceName(voice.gender);
  const normalizedDescription = normalizeVoiceName(voice.description);
  if (target === 'feminine') {
    return normalizedGender.includes('feminine') || normalizedDescription.includes('female');
  }
  return normalizedGender.includes('masculine') || normalizedDescription.includes('male');
};

const describeVoiceSpeed = (speed: number) => {
  if (speed <= 0.8) return 'Measured';
  if (speed >= 1.15) return 'Energetic';
  return 'Natural';
};

const sanitizeVoiceProviderMessage = (value?: string | null) =>
  value?.replace(/cartesia/gi, 'voice provider').trim() || null;

const normalizePronunciationEntries = (entries: PronunciationLibraryEntry[]) =>
  entries
    .map((entry) => ({
      text: entry.text.trim(),
      alias: entry.alias.trim(),
    }))
    .filter((entry) => entry.text.length > 0 || entry.alias.length > 0);

export const SalesAgentContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawVersionParam = searchParams.get('version');
  const versionNumber = rawVersionParam ? Number.parseInt(rawVersionParam, 10) : null;
  const rawBaseParam = searchParams.get('base');
  const rawCompareParam = searchParams.get('compare');
  const rawSelectedParam = searchParams.get('selected');
  const baseVersion = rawBaseParam ? Number.parseInt(rawBaseParam, 10) : null;
  const compareVersion = rawCompareParam ? Number.parseInt(rawCompareParam, 10) : null;
  const selectedVersion = rawSelectedParam ? Number.parseInt(rawSelectedParam, 10) : null;
  const isComparingTwo = baseVersion !== null && Number.isInteger(baseVersion) && baseVersion > 0
    && compareVersion !== null && Number.isInteger(compareVersion) && compareVersion > 0;
  const isViewingVersion = isComparingTwo || (versionNumber !== null && Number.isInteger(versionNumber) && versionNumber > 0);

  // Determine which version is the user's selection and which is the comparison
  // target (changeable via dropdown). Works for both two-version and single-version views.
  const resolvedSelectedVersion = isComparingTwo
    ? (selectedVersion === baseVersion ? baseVersion
      : selectedVersion === compareVersion ? compareVersion
      : compareVersion) // fallback: treat the higher version as selected
    : versionNumber;
  const compareTargetVersion = isComparingTwo && resolvedSelectedVersion !== null
    ? (resolvedSelectedVersion === baseVersion ? compareVersion : baseVersion)
    : null;
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [voiceSearch, setVoiceSearch] = useState('');
  const [styleSearch, setStyleSearch] = useState('');
  const [voiceFilter, setVoiceFilter] = useState<VoiceFilter>('recommended');
  const [voiceEditorPanel, setVoiceEditorPanel] = useState<VoiceEditorPanel>('voice');
  const [voicePage, setVoicePage] = useState(1);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const [activePreviewVoiceId, setActivePreviewVoiceId] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);
  const [pronunciationSearch, setPronunciationSearch] = useState('');
  const [pronunciationEntries, setPronunciationEntries] = useState<PronunciationLibraryEntry[]>([]);

  const [isEditing, setIsEditing] = useState(false);
  const [activeViewSection, setActiveViewSection] = useState<ConfigSection>('general');
  const [activeEditSection, setActiveEditSection] = useState<ConfigSection>('general');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingHeaderLogo, setIsUploadingHeaderLogo] = useState(false);
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  const [isUploadingLauncherIcon, setIsUploadingLauncherIcon] = useState(false);
  const [isUploadingLauncherThumbnail, setIsUploadingLauncherThumbnail] = useState(false);
  const [isUploadingShareThumbnail, setIsUploadingShareThumbnail] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);
  const headerLogoInputRef = React.useRef<HTMLInputElement>(null);
  const backgroundInputRef = React.useRef<HTMLInputElement>(null);
  const launcherIconInputRef = React.useRef<HTMLInputElement>(null);
  const launcherThumbnailInputRef = React.useRef<HTMLInputElement>(null);
  const shareThumbnailInputRef = React.useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<Partial<SalesAgent>>(() => createEmptyFormData());
  const currentSection = isEditing ? activeEditSection : activeViewSection;

  // Fetch existing sales agent
  const { data: salesAgent, isLoading } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.getSalesAgent(orgId);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  const conversationModeOption = getConversationControlModeOption(
    salesAgent?.conversation_control_mode ?? formData.conversation_control_mode
  );

  useEffect(() => {
    if (salesAgent) {
      setFormData(mapSalesAgentToFormData(salesAgent));
      setIsEditing(false);
    } else if (!isLoading) {
      setFormData(createEmptyFormData());
      setIsEditing(false);
    }
  }, [isLoading, salesAgent]);

  const { data: voiceCatalog, isLoading: isVoiceCatalogLoading } = useQuery({
    queryKey: ['cartesia-voices'],
    queryFn: async () => {
      const response = await cartesiaService.listVoices();
      if (!response.success || !response.data) {
        throw new Error(
          sanitizeVoiceProviderMessage(response.error) || 'Failed to load available voices'
        );
      }
      return response.data.voices;
    },
    enabled: !isViewingVersion,
    staleTime: 5 * 60 * 1000,
  });


  const { data: terminologyCountries } = useQuery({
    queryKey: ['terminology-countries', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.getTerminologyCountries(orgId);
      if (response.success && response.data) return response.data;
      return [] as string[];
    },
    enabled: !!orgId && !isViewingVersion,
    staleTime: 10 * 60 * 1000,
  });

  const { data: pronunciationLibrary, isLoading: isPronunciationLibraryLoading } = useQuery({
    queryKey: ['pronunciation-library', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await organizationsService.getPronunciationLibrary(orgId);
      if (!response.success || !response.data) {
        throw new Error(
          sanitizeVoiceProviderMessage(response.error) || 'Failed to load pronunciation library'
        );
      }
      return response.data;
    },
    enabled: !!orgId && !isViewingVersion,
    staleTime: 60 * 1000,
  });

  // Fetch version diff when viewing a specific version
  const { data: versionDiff, isLoading: isVersionLoading } = useQuery({
    queryKey: ['prompt-version-diff', orgId, isComparingTwo ? `${baseVersion}-${compareVersion}` : versionNumber],
    queryFn: async () => {
      if (!orgId) return null;
      if (isComparingTwo) {
        const res = await promptVersionsService.getVersionDiffBetween(orgId, baseVersion!, compareVersion!);
        if (res.success && res.data) return res.data;
        return null;
      }
      if (versionNumber === null) return null;
      const res = await promptVersionsService.getVersionDiff(orgId, versionNumber);
      if (res.success && res.data) return res.data;
      return null;
    },
    enabled: !!orgId && isViewingVersion,
  });

  // Fetch versions list for the comparison target dropdown
  const { data: versionsList } = useQuery({
    queryKey: ['prompt-versions', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const res = await promptVersionsService.listVersions(orgId);
      if (res.success && res.data) return res.data;
      return null;
    },
    enabled: !!orgId && isViewingVersion,
  });

  // Versions available in the "compare against" dropdown (exclude the selected version)
  const compareDropdownOptions = (versionsList?.versions ?? []).filter(
    (v) => v.version !== resolvedSelectedVersion,
  );
  const baseVersionMetadata: PromptVersion | null = versionDiff
    ? (versionsList?.versions ?? []).find((v) => v.version === versionDiff.base_version) ?? null
    : null;
  const compareVersionMetadata: PromptVersion | null = versionDiff
    ? (versionsList?.versions ?? []).find((v) => v.version === versionDiff.compare_version) ?? null
    : null;

  const handleCompareTargetChange = (newTargetValue: string) => {
    const newTarget = Number.parseInt(newTargetValue, 10);
    if (!resolvedSelectedVersion || isNaN(newTarget) || newTarget === compareTargetVersion) return;
    const base = Math.min(newTarget, resolvedSelectedVersion);
    const compare = Math.max(newTarget, resolvedSelectedVersion);
    navigate(
      `/agents/configure?base=${base}&compare=${compare}&selected=${resolvedSelectedVersion}`,
      { replace: true },
    );
  };

  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
      }
    };
  }, [previewAudio]);

  useEffect(() => {
    setPronunciationEntries(pronunciationLibrary?.entries ?? []);
  }, [pronunciationLibrary?.entries]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: SalesAgentUpdate) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.updateSalesAgent(orgId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update sales agent');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-agent', orgId] });
      setIsEditing(false);
      toast({
        title: 'Success',
        description: 'Sales agent updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const savePronunciationLibraryMutation = useMutation({
    mutationFn: async (entries: PronunciationLibraryEntry[]) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await organizationsService.updatePronunciationLibrary(orgId, { entries });
      if (!response.success || !response.data) {
        throw new Error(
          sanitizeVoiceProviderMessage(response.error) || 'Failed to save pronunciation library'
        );
      }
      return response.data;
    },
    onSuccess: (data) => {
      setPronunciationEntries(data.entries || []);
      queryClient.invalidateQueries({ queryKey: ['pronunciation-library', orgId] });
      toast({
        title: 'Pronunciation library saved',
        description: 'Your pronunciation updates will now apply across this organization.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Unable to save pronunciation library',
        description: sanitizeVoiceProviderMessage(error.message) || 'Unable to save pronunciation library.',
        variant: 'destructive',
      });
    },
  });

  const englishVoices = (voiceCatalog || []).filter((voice) => {
    const language = voice.language?.toLowerCase();
    return language === 'en' || language?.startsWith('en_') || language?.startsWith('en-');
  });

  const professionalEnglishVoices = englishVoices.filter((voice) => {
    const normalizedMetadata = normalizeVoiceName(`${voice.name || ''} ${voice.description || ''}`);
    return !NON_PROFESSIONAL_VOICE_KEYWORDS.some((keyword) => normalizedMetadata.includes(keyword));
  });

  const recommendedVoices = professionalEnglishVoices.filter((voice) => {
    const normalizedName = normalizeVoiceName(voice.name);
    return RECOMMENDED_VOICE_KEYWORDS.some((keyword) => normalizedName.includes(keyword));
  });
  const feminineVoices = professionalEnglishVoices.filter((voice) => matchesVoiceGender(voice, 'feminine'));
  const masculineVoices = professionalEnglishVoices.filter((voice) => matchesVoiceGender(voice, 'masculine'));
  const usingRecommendedFallback = voiceFilter === 'recommended' && recommendedVoices.length === 0;
  const visibleVoices = (() => {
    if (voiceFilter === 'recommended') {
      return usingRecommendedFallback ? professionalEnglishVoices : recommendedVoices;
    }
    if (voiceFilter === 'feminine') return feminineVoices;
    if (voiceFilter === 'masculine') return masculineVoices;
    return professionalEnglishVoices;
  })();

  const filteredVoices = visibleVoices.filter((voice) => {
    const query = voiceSearch.trim().toLowerCase();
    if (!query) return true;
    return [voice.name, voice.description, voice.language, voice.gender]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(query));
  });

  const selectedVoiceId = formData.voice_settings?.voice_id?.trim() || '';
  const selectedVoice = englishVoices.find((voice) => voice.id === selectedVoiceId)
    || (voiceCatalog || []).find((voice) => voice.id === selectedVoiceId)
    || null;
  const savedVoiceId = salesAgent?.voice_settings?.voice_id?.trim() || '';
  const savedVoice = englishVoices.find((voice) => voice.id === savedVoiceId)
    || (voiceCatalog || []).find((voice) => voice.id === savedVoiceId)
    || null;
  const selectedVoiceLabel = selectedVoice?.name || selectedVoiceId || 'No voice selected';
  const savedVoiceLabel = savedVoice?.name || savedVoiceId || 'No voice selected';
  const savedVoiceLanguage = savedVoice?.language?.trim() || null;
  const selectedEmotion = (formData.voice_settings?.emotion ?? '').trim() || 'confident';
  const selectedSpeed = formData.voice_settings?.speed ?? 0.9;
  const voicePanelCardClass = (panel: VoiceEditorPanel) => cn(
    'p-3 rounded-lg border text-left',
    voiceEditorPanel === panel
      ? 'border-primary bg-primary/5'
      : 'card-border bg-gray-50/50 dark:bg-zinc-900/50'
  );
  const totalVoicePages = Math.max(1, Math.ceil(filteredVoices.length / VOICES_PER_PAGE));
  const paginatedVoices = filteredVoices.slice((voicePage - 1) * VOICES_PER_PAGE, voicePage * VOICES_PER_PAGE);
  const filteredStyles = VOICE_EMOTIONS.filter((emotion) => {
    const query = styleSearch.trim().toLowerCase();
    if (!query) return true;
    return [emotion.label, emotion.value, emotion.description]
      .some((value) => value.toLowerCase().includes(query));
  });
  const filteredPronunciationRows = pronunciationEntries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => {
      const query = pronunciationSearch.trim().toLowerCase();
      if (!query) return true;
      return [entry.text, entry.alias].some((value) => value.toLowerCase().includes(query));
    });
  const persistedPronunciationEntries = normalizePronunciationEntries(pronunciationLibrary?.entries ?? []);
  const savedPronunciationEntries = persistedPronunciationEntries;
  const draftPronunciationEntries = normalizePronunciationEntries(pronunciationEntries);
  const pronunciationLibraryDirty =
    JSON.stringify(draftPronunciationEntries) !== JSON.stringify(persistedPronunciationEntries);
  const hasIncompletePronunciationRow = pronunciationEntries.some((entry) => {
    const hasAnyValue = entry.text.trim().length > 0 || entry.alias.trim().length > 0;
    const hasBothValues = entry.text.trim().length > 0 && entry.alias.trim().length > 0;
    return hasAnyValue && !hasBothValues;
  });

  const updateVoiceSettings = (updates: Partial<VoiceSettings> | null) => {
    setFormData((prev) => {
      const current = prev.voice_settings ?? {
        voice_id: '',
        speed: 0.9,
        emotion: 'confident',
      };

      if (updates === null) {
        return { ...prev, voice_settings: null };
      }

      const nextVoiceSettings: VoiceSettings = {
        ...current,
        ...updates,
      };

      if (!nextVoiceSettings.voice_id) {
        return { ...prev, voice_settings: null };
      }

      return { ...prev, voice_settings: nextVoiceSettings };
    });
  };

  const addPronunciationEntry = () => {
    if (pronunciationEntries.length >= MAX_PRONUNCIATION_ENTRIES) {
      toast({
        title: 'Entry limit reached',
        description: `You can add up to ${MAX_PRONUNCIATION_ENTRIES} pronunciation entries.`,
        variant: 'destructive',
      });
      return;
    }
    setPronunciationEntries((prev) => [...prev, { text: '', alias: '' }]);
  };

  const removePronunciationEntry = (index: number) => {
    setPronunciationEntries((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const updatePronunciationEntry = (
    index: number,
    field: keyof PronunciationLibraryEntry,
    value: string
  ) => {
    setPronunciationEntries((prev) =>
      prev.map((entry, currentIndex) =>
        currentIndex === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const savePronunciationLibrary = () => {
    const normalizedEntries = normalizePronunciationEntries(pronunciationEntries);

    const incompleteEntry = normalizedEntries.find((entry) => !entry.text || !entry.alias);
    if (incompleteEntry) {
      toast({
        title: 'Finish each pronunciation row',
        description: 'Each entry needs both a word/phrase and a pronunciation value.',
        variant: 'destructive',
      });
      return;
    }

    const uniqueTexts = new Set<string>();
    for (const entry of normalizedEntries) {
      const key = entry.text.toLowerCase();
      if (uniqueTexts.has(key)) {
        toast({
          title: 'Duplicate word/phrase',
          description: `"${entry.text}" appears more than once. Keep one row per word/phrase.`,
          variant: 'destructive',
        });
        return;
      }
      uniqueTexts.add(key);
    }

    savePronunciationLibraryMutation.mutate(normalizedEntries);
  };

  useEffect(() => {
    setVoicePage(1);
  }, [voiceSearch, voiceFilter]);

  useEffect(() => {
    if (voicePage > totalVoicePages) {
      setVoicePage(totalVoicePages);
    }
  }, [voicePage, totalVoicePages]);

  const selectVoice = (voice: CartesiaVoice) => {
    if (previewAudio) {
      previewAudio.pause();
      setActivePreviewVoiceId(null);
    }
    updateVoiceSettings({
      voice_id: voice.id,
    });
  };

  const handleVoicePreview = async (voice: CartesiaVoice) => {
    if (activePreviewVoiceId === voice.id && previewAudio) {
      previewAudio.pause();
      setActivePreviewVoiceId(null);
      return;
    }

    try {
      setPreviewingVoiceId(voice.id);
      if (previewAudio) {
        previewAudio.pause();
      }

      const authToken = apiClient.getAuthToken();
      const previewParams = new URLSearchParams({
        speed: selectedSpeed.toFixed(2),
        emotion: selectedEmotion,
      });
      if (orgId) {
        previewParams.set('organization_id', orgId);
      }
      const response = await fetch(
        `${apiClient.getBaseUrl()}/api/integrations/cartesia/voices/${voice.id}/preview?${previewParams.toString()}`,
        {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        }
      );

      if (!response.ok) {
        throw new Error(`Preview failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audio.onended = () => {
        URL.revokeObjectURL(objectUrl);
        setActivePreviewVoiceId((current) => (current === voice.id ? null : current));
      };

      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setActivePreviewVoiceId(null);
      };

      setPreviewAudio(audio);
      setActivePreviewVoiceId(voice.id);
      await audio.play();
    } catch (err) {
      toast({
        title: 'Preview unavailable',
        description: err instanceof Error ? err.message : 'Unable to play this voice preview right now.',
        variant: 'destructive',
      });
      setActivePreviewVoiceId(null);
    } finally {
      setPreviewingVoiceId(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!salesAgent) {
      toast({
        title: 'Agent not ready',
        description: 'Create an agent from Versions before making changes here.',
        variant: 'destructive',
      });
      return;
    }

    if (formData.voice_settings?.emotion?.trim() && !VALID_VOICE_EMOTIONS.has(formData.voice_settings.emotion.trim().toLowerCase())) {
      toast({
        title: 'Unsupported speaking style',
        description: 'Use one of the supported voice styles for now, or pick from the suggested options below.',
        variant: 'destructive',
      });
      return;
    }

    const payload: SalesAgentUpdate = {
      is_active: formData.is_active ?? true,
      conversation_control_mode: formData.conversation_control_mode || 'balanced',
      use_active_deck: formData.use_active_deck !== false,
      use_all_videos: formData.use_all_videos !== false,
      use_documentation: formData.use_documentation === true,
      contact_email: formData.contact_email || '',
      calendar_link: formData.calendar_link || '',
      suggestion_instructions: formData.suggestion_instructions || '',
      auto_suggestions_enabled: formData.auto_suggestions_enabled !== false,
      entry_modal_logo_url: formData.entry_modal_logo_url || '',
      header_logo_url: formData.header_logo_url || '',
      background_screenshot_url: formData.background_screenshot_url || '',
      share_title: formData.share_title || null,
      share_description: formData.share_description || null,
      share_thumbnail_url: formData.share_thumbnail_url || null,
      sticky_trigger_icon_url: formData.sticky_trigger_icon_url || '',
      sticky_trigger_text: formData.sticky_trigger_text || '',
      sticky_trigger_bg_color: formData.sticky_trigger_bg_color || '',
      sticky_trigger_font_family: formData.sticky_trigger_font_family || null,
      sticky_trigger_thumbnail_url: formData.sticky_trigger_thumbnail_url || '',
      launcher_icon_bg_color: formData.launcher_icon_bg_color || '',
      voice_settings: formData.voice_settings?.voice_id
        ? {
            voice_id: formData.voice_settings.voice_id,
            speed: formData.voice_settings.speed ?? 0.9,
            emotion: formData.voice_settings.emotion?.trim() ? formData.voice_settings.emotion.trim().toLowerCase() : 'confident',
          }
        : null,
      terminology_bank: formData.terminology_bank || null,
    };

    updateMutation.mutate(payload);
  };

  const handleImageUpload = async (
    file: File,
    field: 'entry_modal_logo_url' | 'header_logo_url' | 'background_screenshot_url' | 'sticky_trigger_icon_url' | 'sticky_trigger_thumbnail_url' | 'share_thumbnail_url',
    fileType: string,
    setUploading: (v: boolean) => void,
  ) => {
    if (!orgId) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Allowed formats: PNG, JPG, WebP, GIF, SVG', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 5 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('organizationId', orgId);
      formPayload.append('fileType', fileType);
      formPayload.append('file', file);
      if (salesAgent?.id) formPayload.append('agentKey', salesAgent.id);
      const res = await apiClient.post<FileUploadResponse>('/api/storage/upload', formPayload);
      if (!res.success || !res.data?.url) {
        throw new Error(res.error || 'Upload failed');
      }
      setFormData((prev) => ({ ...prev, [field]: res.data!.url }));
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  if (isLoading || !currentOrganization) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-subtle">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>Loading sales agent...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isViewingVersion && !salesAgent) {
    return (
      <div className="flex-1">
        <div className="px-4 sm:px-6 md:px-8 pb-8 pt-4">
          <div className="max-w-3xl overflow-hidden rounded-lg border border-border bg-background shadow-sm">
            <div className="border-b border-border px-6 py-5">
              <h2 className="text-2xl font-semibold tracking-tight text-heading">Agent</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Manage how your agent runs and what visitors see before a session starts.
              </p>
            </div>

            <div className="px-6 py-6">
              <div className="grid gap-5 rounded-lg border border-border bg-card p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-heading">No agent yet</p>
                  <p className="text-sm text-muted-foreground">Create your agent to start running sessions.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={() => navigate('/agents/create-version')}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Create Agent
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/agents')}
                  >
                    Back to Agent Versions
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1">
        <div className="flex-1 px-4 py-6 sm:px-6 md:px-8">
          <Tabs
            value={currentSection}
            onValueChange={(value) => {
              const section = value as ConfigSection;
              if (isEditing) {
                setActiveEditSection(section);
              } else {
                setActiveViewSection(section);
              }
            }}
            className="flex flex-col"
          >
            <div className="flex flex-col">
              {!isViewingVersion && (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                  <TabsList>
                    {CONFIG_SECTIONS.map((section) => (
                      <TabsTrigger key={section.key} value={section.key}>
                        {section.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  <div className="flex flex-wrap items-center gap-2">
                    {isEditing ? (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsEditing(false);
                            setFormData(mapSalesAgentToFormData(salesAgent));
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          form="agent-form"
                          size="sm"
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving
                            </>
                          ) : (
                            <>
                              <Save className="mr-2 h-4 w-4" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          setFormData(mapSalesAgentToFormData(salesAgent));
                          setIsEditing(true);
                          setActiveEditSection(activeViewSection);
                        }}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
              )}
                {isViewingVersion ? (
                  <div className="space-y-4">
                    {resolvedSelectedVersion !== null && compareDropdownOptions.length > 0 && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          Comparing v{resolvedSelectedVersion} against:
                        </span>
                        <Select
                          value={compareTargetVersion !== null ? String(compareTargetVersion) : undefined}
                          onValueChange={handleCompareTargetChange}
                        >
                          <SelectTrigger className="w-[180px] h-9">
                            <SelectValue placeholder="Select version" />
                          </SelectTrigger>
                          <SelectContent>
                            {compareDropdownOptions.map((v) => (
                              <SelectItem key={v.version} value={String(v.version)}>
                                v{v.version}
                                {v.status === 'live' ? ' (Live)' : v.status === 'staging' ? ' (Staging)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isVersionLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : versionDiff ? (
                      <PromptDiffView
                        data={versionDiff}
                        baseVersion={baseVersionMetadata}
                        compareVersion={compareVersionMetadata}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Unable to load version diff.</p>
                    )}
                  </div>
                ) : salesAgent && !isEditing ? (
                  <div className={cn(activeViewSection === 'branding' ? 'w-full max-w-none' : 'max-w-4xl', 'space-y-4')}>
                    {activeViewSection === 'general' && (
                        <ConfigSurface>
                          <ConfigRow
                            label="Agent Availability"
                            description="Enable or disable the agent for production use."
                            action={(
                              <Switch
                                checked={salesAgent.is_active}
                                onCheckedChange={() => {}}
                                className="data-[state=checked]:bg-primary disabled:opacity-100"
                                disabled
                              />
                            )}
                          >
                            <span className="text-muted-foreground">
                              {salesAgent.is_active ? 'Available for production sessions.' : 'Paused for production sessions.'}
                            </span>
                          </ConfigRow>
                          <ConfigRow label="Contact Email" description="Used for follow-up and contact routing.">
                            <ConfigValue>{salesAgent.contact_email || 'No contact email configured'}</ConfigValue>
                          </ConfigRow>
                          <ConfigRow label="Booking Link" description="Booking destination for follow-up meetings.">
                            <ConfigValue>{salesAgent.calendar_link || 'Using Hobbes default (cal.com/hobbes)'}</ConfigValue>
                          </ConfigRow>
                        </ConfigSurface>
                    )}

                    {activeViewSection === 'branding' && (
                      <div className="grid w-full max-w-none gap-4 lg:grid-cols-[minmax(440px,0.38fr)_minmax(0,1.62fr)] lg:items-start">
                        <div className="space-y-4">
                          <ConfigGroup title="Brand Assets">
                            <ConfigSurface className="max-w-none">
                            <ConfigRow label="Entry Wordmark" description="Brand mark shown in the agent entry prompt.">
                            {salesAgent.entry_modal_logo_url ? (
                              <div className="inline-flex rounded-lg border border-border/70 p-1" style={TRANSPARENCY_GRID_STYLE}>
                                <img src={salesAgent.entry_modal_logo_url} alt="Entry wordmark" className="h-12 w-auto rounded-lg object-contain" />
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Default logo</span>
                            )}
                          </ConfigRow>
                          <ConfigRow label="Active Wordmark" description="Light brand mark shown in the active agent header. Falls back to the entry wordmark when empty.">
                            {salesAgent.header_logo_url ? (
                              <div className="inline-flex rounded-lg border border-border/70 bg-zinc-900 p-1">
                                <img src={salesAgent.header_logo_url} alt="Active wordmark" className="h-12 w-auto rounded-lg object-contain" />
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Falls back to entry wordmark</span>
                            )}
                          </ConfigRow>
                          <ConfigRow label="Icon" description="Square icon displayed inside the Agent Button.">
                            {salesAgent.sticky_trigger_icon_url ? (
                              <div className="inline-flex rounded-full border border-border/70 p-1" style={TRANSPARENCY_GRID_STYLE}>
                                <img src={salesAgent.sticky_trigger_icon_url} alt="Agent icon" className="h-10 w-10 rounded-full object-cover" />
                              </div>
                            ) : (
                              <span className="text-muted-foreground">No icon configured</span>
                            )}
                          </ConfigRow>
                            </ConfigSurface>
                          </ConfigGroup>

                          <ConfigGroup title="Agent Button">
                            <ConfigSurface className="max-w-none">
                          <ConfigRow label="Button Text" description="Short call-to-action shown in the Agent Button.">
                            <ConfigValue>{salesAgent.sticky_trigger_text || 'Start Demo'}</ConfigValue>
                          </ConfigRow>
                          <ConfigRow label="Font Family" description="Typeface used for the Agent Button text.">
                            <ConfigValue>{salesAgent.sticky_trigger_font_family || 'Default (system font)'}</ConfigValue>
                          </ConfigRow>
                          <ConfigRow label="Tint" description="Background color used for the Agent Button.">
                            <div className="flex items-center gap-2">
                              <span
                                aria-hidden
                                className="h-3.5 w-3.5 rounded-full border border-border"
                                style={{ backgroundColor: normalizeHexColor(salesAgent.sticky_trigger_bg_color) || DEFAULT_LAUNCHER_TINT }}
                              />
                              <span className="text-heading">{normalizeHexColor(salesAgent.sticky_trigger_bg_color) || 'Default'}</span>
                            </div>
                          </ConfigRow>
                          <ConfigRow label="Icon Background" description="Color of the small circle behind the Agent Button icon.">
                            <div className="flex items-center gap-2">
                              <span
                                aria-hidden
                                className="h-3.5 w-3.5 rounded-full border border-border"
                                style={{ backgroundColor: normalizeHexColor(salesAgent.launcher_icon_bg_color) || DEFAULT_LAUNCHER_ICON_BG }}
                              />
                              <span className="text-heading">{normalizeHexColor(salesAgent.launcher_icon_bg_color) || 'Default'}</span>
                            </div>
                          </ConfigRow>
                          <ConfigRow label="Hover Thumbnail" description="Optional 16:9 image shown when visitors hover the Agent Button.">
                            {salesAgent.sticky_trigger_thumbnail_url ? (
                              <div className="w-full max-w-xs overflow-hidden rounded-lg border border-border/70" style={TRANSPARENCY_GRID_STYLE}>
                                <div className="aspect-video w-full">
                                  <img src={salesAgent.sticky_trigger_thumbnail_url} alt="Agent Button hover thumbnail" className="h-full w-full object-cover" />
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">No hover thumbnail configured</span>
                            )}
                          </ConfigRow>
                            </ConfigSurface>
                          </ConfigGroup>

                          <ConfigGroup title="Link Previews">
                            <ConfigSurface className="max-w-none">
                              <ConfigRow label="Default Link Title" description="Title shown when the main demo link is shared.">
                                <ConfigValue>{salesAgent.share_title || `${salesAgent.company_name || salesAgent.name || 'Company'} Demo`}</ConfigValue>
                              </ConfigRow>
                              <ConfigRow label="Default Link Description" description="Description shown in email and social previews.">
                                <ConfigValue>
                                  {salesAgent.share_description || `Explore an interactive product demo from ${salesAgent.company_name || salesAgent.name || 'this company'}.`}
                                </ConfigValue>
                              </ConfigRow>
                              <ConfigRow label="Default Link Thumbnail" description="16:9 image shown when the main demo link is shared.">
                                {salesAgent.share_thumbnail_url ? (
                                  <div className="w-full max-w-xs overflow-hidden rounded-lg border border-border/70" style={TRANSPARENCY_GRID_STYLE}>
                                    <div className="aspect-video w-full">
                                      <img src={salesAgent.share_thumbnail_url} alt="Default link thumbnail" className="h-full w-full object-cover" />
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">Uses demo image fallback</span>
                                )}
                              </ConfigRow>
                            </ConfigSurface>
                          </ConfigGroup>

                          <ConfigGroup title="Start Screen">
                            <ConfigSurface className="max-w-none">
                            <ConfigRow label="Start Screen" description="Image shown before the first piece of demo content loads." className="md:items-start">
                            <ConfigPreviewSlot className="min-h-[260px]">
                              {salesAgent.background_screenshot_url ? (
                                <div className="flex w-full items-center justify-center rounded-lg" style={TRANSPARENCY_GRID_STYLE}>
                                  <img
                                    src={salesAgent.background_screenshot_url}
                                    alt="Start screen preview"
                                    className="max-h-[220px] w-auto max-w-full rounded-lg object-contain"
                                  />
                                </div>
                              ) : (
                                <ConfigEmptyState icon={<Image className="mx-auto h-10 w-10 text-muted-foreground/50" />} title="No start screen set" />
                              )}
                            </ConfigPreviewSlot>
                          </ConfigRow>
                            </ConfigSurface>
                          </ConfigGroup>
                        </div>

                        <div className="lg:sticky lg:top-20">
                          <ExperiencePreviewPanel
                            iconUrl={salesAgent.sticky_trigger_icon_url || ''}
                            text={salesAgent.sticky_trigger_text || ''}
                            tintColor={salesAgent.sticky_trigger_bg_color || null}
                            textColor={salesAgent.sticky_trigger_text_color || null}
                            fontFamily={salesAgent.sticky_trigger_font_family || null}
                            thumbnailUrl={salesAgent.sticky_trigger_thumbnail_url || ''}
                            leadCaptureEmail={salesAgent.lead_capture_email}
                            iconBgColor={salesAgent.launcher_icon_bg_color || null}
                            activeScreenshotUrl={salesAgent.background_screenshot_url || null}
                            headerLogoUrl={salesAgent.header_logo_url || salesAgent.entry_modal_logo_url || null}
                            entryModalLogoUrl={salesAgent.entry_modal_logo_url || null}
                            companyName={salesAgent.company_name || salesAgent.name || null}
                          />
                        </div>
                      </div>
                    )}

                    {activeViewSection === 'conversation' && (
                      <>
                        <ConfigSurface>
                          <ConfigRow label="Selected Voice" description="Primary voice used for live sessions.">
                            <ConfigValue>{savedVoiceLabel}</ConfigValue>
                          </ConfigRow>
                          <ConfigRow label="Speaking Style" description="Emotion profile applied to playback.">
                            <ConfigValue className="capitalize">{salesAgent.voice_settings?.emotion || 'confident'}</ConfigValue>
                          </ConfigRow>
                          <ConfigRow label="Speed" description="Cadence used for voice responses.">
                            <ConfigValue>{(salesAgent.voice_settings?.speed ?? 0.9).toFixed(2)} - {describeVoiceSpeed(salesAgent.voice_settings?.speed ?? 0.9)}</ConfigValue>
                          </ConfigRow>
                          <ConfigRow label="Voice Details" description="Catalog metadata for the current voice.">
                            <div className="space-y-2">
                              <ConfigValue><span className="break-all">{salesAgent.voice_settings?.voice_id || 'Not configured'}</span></ConfigValue>
                              {savedVoiceLanguage && <p className="text-sm text-muted-foreground">Language: {savedVoiceLanguage}</p>}
                            </div>
                          </ConfigRow>
                          <ConfigRow
                            label="Voice Preview"
                            description="Hear the current configuration exactly as it will sound in live voice sessions."
                            action={savedVoice ? (
                              <Button
                                type="button"
                                variant="outline"
                                className="h-9 gap-2 rounded-lg"
                                onClick={() => handleVoicePreview(savedVoice)}
                                disabled={previewingVoiceId === savedVoice.id}
                              >
                                {previewingVoiceId === savedVoice.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : activePreviewVoiceId === savedVoice.id ? (
                                  <Pause className="h-4 w-4" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                                {activePreviewVoiceId === savedVoice.id ? 'Pause Preview' : 'Play Preview'}
                              </Button>
                            ) : null}
                          >
                            <span className="text-muted-foreground">{salesAgent.voice_settings?.voice_id || 'No voice configured'}</span>
                          </ConfigRow>
                        </ConfigSurface>

                        <ConfigSurface>
                          <ConfigRow label="Conversation Approach" description="How strongly the agent should lead the demo once discovery is complete." action={<Badge variant="secondary">{conversationModeOption.label}</Badge>}>
                            <span className="text-muted-foreground">{conversationModeOption.description}</span>
                          </ConfigRow>
                          <ConfigRow
                            label="Suggested Follow-ups"
                            description="Show follow-up suggestion pills to visitors during live sessions."
                            action={<Badge variant={salesAgent.auto_suggestions_enabled ? 'default' : 'secondary'}>{salesAgent.auto_suggestions_enabled ? 'Enabled' : 'Disabled'}</Badge>}
                          >
                            <span className="text-muted-foreground">
                              {salesAgent.auto_suggestions_enabled ? 'Suggestion chips are shown after each agent response.' : 'Suggestion chips are hidden during sessions.'}
                            </span>
                          </ConfigRow>
                          {salesAgent.auto_suggestions_enabled && (
                            <ConfigRow label="Follow-up Guidance" description="Custom instructions for the follow-up suggestion chips shown during the demo.">
                              <ConfigValue className="whitespace-pre-wrap">
                                {salesAgent.suggestion_instructions || 'No custom suggestion guidance configured.'}
                              </ConfigValue>
                            </ConfigRow>
                          )}
                        </ConfigSurface>

                        <ConfigSurface>
                          <ConfigRow label="Pronunciation Library" description="Organization-level pronunciation overrides applied to this agent.">
                            {isPronunciationLibraryLoading ? (
                              <div className="flex items-center justify-center rounded-lg border border-dashed border-border/70 bg-background/40 py-8 text-muted-foreground">
                                <Loader2 className="h-5 w-5 animate-spin" />
                              </div>
                            ) : savedPronunciationEntries.length === 0 ? (
                              <ConfigValue>No pronunciation entries configured.</ConfigValue>
                            ) : (
                              <div className="overflow-hidden rounded-lg border border-border/70">
                                <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1fr)] border-b border-border/70 bg-gray-50/60 px-3 py-2 text-xs font-medium text-subtle dark:bg-zinc-900/50 md:grid">
                                  <span>Word or phrase</span>
                                  <span>Pronunciation</span>
                                </div>
                                <div className="divide-y divide-border/60">
                                  {savedPronunciationEntries.map((entry, index) => (
                                    <div key={`saved-pronunciation-row-${index}`} className="grid grid-cols-1 gap-2 px-3 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-3">
                                      <div>
                                        <Label className="text-[11px] text-subtle md:hidden">Word or phrase</Label>
                                        <p className="text-sm text-body">{entry.text}</p>
                                      </div>
                                      <div>
                                        <Label className="text-[11px] text-subtle md:hidden">Pronunciation</Label>
                                        <p className="font-mono text-sm text-body">{entry.alias}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </ConfigRow>
                          <ConfigRow label="Terminology" description="Region-specific terms and wording preferences for the agent.">
                            {salesAgent.terminology_bank && Object.keys(salesAgent.terminology_bank).length > 0 ? (
                              <div className="space-y-3">
                                {Object.entries(salesAgent.terminology_bank).map(([country, entries]) => (
                                  <div key={country} className="overflow-hidden rounded-lg border border-border/70">
                                    <div className="border-b border-border/70 bg-gray-50/60 px-3 py-2 text-sm font-medium text-heading dark:bg-zinc-900/50">
                                      {country}
                                    </div>
                                    <div className="divide-y divide-border/60">
                                      {(entries as TerminologyEntry[]).map((entry, idx) => (
                                        <div key={idx} className="grid gap-2 px-3 py-3 md:grid-cols-[180px_minmax(0,1fr)]">
                                          <p className="text-sm font-medium text-heading">{entry.term}</p>
                                          <p className="text-sm text-muted-foreground">{entry.context || '-'}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <ConfigValue>No terminology configured.</ConfigValue>
                            )}
                          </ConfigRow>
                        </ConfigSurface>
                      </>
                    )}

                    {activeViewSection === 'collateralAccess' && (
                      <ConfigSurface>
                        <ConfigRow label="Active Deck" description="Allow the agent to reference the active slide deck." action={<Badge variant={salesAgent.use_active_deck ? 'default' : 'secondary'}>{salesAgent.use_active_deck ? 'Enabled' : 'Disabled'}</Badge>}>
                          <span className="text-muted-foreground">Presentation content is {salesAgent.use_active_deck ? 'available' : 'excluded'} during sessions.</span>
                        </ConfigRow>
                        <ConfigRow label="Videos" description="Allow the agent to search video content." action={<Badge variant={salesAgent.use_all_videos ? 'default' : 'secondary'}>{salesAgent.use_all_videos ? 'Enabled' : 'Disabled'}</Badge>}>
                          <span className="text-muted-foreground">Video assets are {salesAgent.use_all_videos ? 'available' : 'excluded'} in the live experience.</span>
                        </ConfigRow>
                        <ConfigRow label="Documentation" description="Allow the agent to show documentation during sessions." action={<Badge variant={salesAgent.use_documentation ? 'default' : 'secondary'}>{salesAgent.use_documentation ? 'Enabled' : 'Disabled'}</Badge>}>
                          <span className="text-muted-foreground">Documentation pages are {salesAgent.use_documentation ? 'available' : 'excluded'} in the live experience.</span>
                        </ConfigRow>
                      </ConfigSurface>
                    )}
                  </div>
                ) : (
                  <form id="agent-form" onSubmit={handleSubmit} className="h-full">
                    <div className={cn(activeEditSection === 'branding' ? 'w-full max-w-none' : 'max-w-4xl', 'space-y-4')}>
                      <TabsContent value="general" className="mt-0 space-y-4">
                        <ConfigSurface>
                          <ConfigRow
                            htmlFor="is_active"
                            label="Agent Availability"
                            description="Enable or disable the agent for production use."
                            action={(
                            <Switch
                              id="is_active"
                              checked={formData.is_active || false}
                              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                            )}
                          >
                            <span className="text-muted-foreground">
                              {formData.is_active ? 'Available for production sessions.' : 'Paused for production sessions.'}
                            </span>
                          </ConfigRow>
                          <ConfigRow htmlFor="contact_email" label="Contact Email" description="Used for follow-up and contact routing.">
                            <Input
                              id="contact_email"
                              type="email"
                              value={formData.contact_email || ''}
                              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                              placeholder="team@yourcompany.com"
                              className="h-9 rounded-lg"
                            />
                          </ConfigRow>
                          <ConfigRow htmlFor="calendar_link" label="Booking Link" description="Booking destination used for follow-up meetings.">
                            <Input
                              id="calendar_link"
                              type="url"
                              value={formData.calendar_link || ''}
                              onChange={(e) => setFormData({ ...formData, calendar_link: e.target.value })}
                              placeholder="https://cal.com/your-company/demo"
                              className="h-9 rounded-lg"
                            />
                          </ConfigRow>
                        </ConfigSurface>
                      </TabsContent>

                      <TabsContent value="conversation" className="mt-0 space-y-4">
                        <div className="max-w-4xl">
                          <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('voice')}
                                className={voicePanelCardClass('voice')}
                                aria-pressed={voiceEditorPanel === 'voice'}
                              >
                                <ConfigFieldLabel label="Selected Voice" description="Primary voice used for live sessions." />
                                <p className="mt-2 text-sm font-medium">{selectedVoiceLabel}</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('style')}
                                className={voicePanelCardClass('style')}
                                aria-pressed={voiceEditorPanel === 'style'}
                              >
                                <ConfigFieldLabel label="Speaking Style" description="Emotion profile applied to playback." />
                                <p className="mt-2 text-sm font-medium capitalize">{selectedEmotion}</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('speed')}
                                className={voicePanelCardClass('speed')}
                                aria-pressed={voiceEditorPanel === 'speed'}
                              >
                                <ConfigFieldLabel label="Speed" description="Cadence used for voice responses." />
                                <p className="mt-2 text-sm font-medium">{selectedSpeed.toFixed(2)} - {describeVoiceSpeed(selectedSpeed)}</p>
                              </button>
                            </div>

                            <div className="rounded-lg border border-border/80 bg-background/60 p-4">
                              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div>
                                  <ConfigFieldLabel label="Voice Preview" description="Hear the current configuration exactly as it will sound in live voice sessions." />
                                  <p className="text-sm text-body">
                                    {selectedVoice
                                      ? `Play ${selectedVoice.name} using ${selectedEmotion} at ${selectedSpeed.toFixed(2)} speed.`
                                      : 'Select a voice to preview the current speaking style and speed.'}
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="gap-2 md:self-start"
                                  onClick={() => selectedVoice && handleVoicePreview(selectedVoice)}
                                  disabled={!selectedVoice || previewingVoiceId === selectedVoice.id}
                                >
                                  {selectedVoice && previewingVoiceId === selectedVoice.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : selectedVoice && activePreviewVoiceId === selectedVoice.id ? (
                                    <Pause className="h-4 w-4" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                  {selectedVoice && activePreviewVoiceId === selectedVoice.id ? 'Pause Sample' : 'Play Sample'}
                                </Button>
                              </div>
                            </div>

                            {voiceEditorPanel === 'voice' && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between gap-3">
                                <Label className="text-sm font-semibold">Available Voices</Label>
                              </div>
                              <Tabs
                                value={voiceFilter}
                                onValueChange={(value) => setVoiceFilter(value as VoiceFilter)}
                                className="w-full"
                              >
                                <TabsList className="inline-flex h-9 w-auto">
                                  <TabsTrigger value="recommended">Recommended</TabsTrigger>
                                  <TabsTrigger value="all">All</TabsTrigger>
                                  <TabsTrigger value="feminine">Feminine</TabsTrigger>
                                  <TabsTrigger value="masculine">Masculine</TabsTrigger>
                                </TabsList>
                              </Tabs>
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  value={voiceSearch}
                                  onChange={(e) => setVoiceSearch(e.target.value)}
                                  placeholder="Search voices"
                                  className="h-10 pl-9"
                                />
                              </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              {isVoiceCatalogLoading ? (
                                <div className="col-span-full flex items-center justify-center rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 py-10 text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                </div>
                              ) : filteredVoices.length === 0 ? (
                                <div className="col-span-full rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 p-6 text-center">
                                  <p className="text-sm font-medium text-heading">No voices match this filter</p>
                                  <p className="mt-1 text-sm text-subtle">Try a different search term or switch tabs.</p>
                                </div>
                              ) : (
                                paginatedVoices.map((voice) => {
                                  const isSelected = formData.voice_settings?.voice_id === voice.id;
                                  return (
                                    <div
                                      key={voice.id}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => selectVoice(voice)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          selectVoice(voice);
                                        }
                                      }}
                                      className={cn(
                                        'rounded-lg border p-4',
                                        isSelected
                                          ? 'border-primary bg-primary/5'
                                          : 'card-border bg-gray-50/30 dark:bg-zinc-900/30'
                                      )}
                                    >
                                      <div className="space-y-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <p className="text-sm font-semibold text-heading">{voice.name}</p>
                                          {isSelected && (
                                            <Badge className="text-[10px] uppercase tracking-wide">Selected</Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-subtle line-clamp-2">
                                          {voice.description || 'Voice preview available.'}
                                        </p>
                                      </div>

                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {voice.language && (
                                          <Badge variant="outline" className="gap-1">
                                            <Globe2 className="h-3 w-3" />
                                            {voice.language}
                                          </Badge>
                                        )}
                                        {voice.gender && (
                                          <Badge variant="outline">{voice.gender}</Badge>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            {filteredVoices.length > 0 && (
                              <div className="flex items-center justify-between pt-1">
                                <p className="text-xs text-subtle">
                                  Showing {(voicePage - 1) * VOICES_PER_PAGE + 1}-{Math.min(voicePage * VOICES_PER_PAGE, filteredVoices.length)} of {filteredVoices.length}
                                </p>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setVoicePage((page) => Math.max(1, page - 1))}
                                    disabled={voicePage === 1}
                                    className="h-8 px-2"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </Button>
                                  <span className="text-xs text-subtle">
                                    Page {voicePage} of {totalVoicePages}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setVoicePage((page) => Math.min(totalVoicePages, page + 1))}
                                    disabled={voicePage === totalVoicePages}
                                    className="h-8 px-2"
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            )}
                            </div>
                            )}

                            {voiceEditorPanel === 'style' && (
                            <div className="space-y-3">
                              <Label className="text-sm font-semibold">Search Styles</Label>
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  value={styleSearch}
                                  onChange={(e) => setStyleSearch(e.target.value)}
                                  placeholder="Search styles"
                                  className="h-10 pl-9"
                                />
                              </div>
                              <div className="grid gap-3 md:grid-cols-2">
                                {filteredStyles.map((emotion) => {
                                  const isActive = selectedEmotion === emotion.value;
                                  return (
                                    <button
                                      key={emotion.value}
                                      type="button"
                                      onClick={() => updateVoiceSettings({ emotion: emotion.value, voice_id: formData.voice_settings?.voice_id || '' })}
                                      className={cn(
                                        'rounded-lg border p-4 text-left',
                                        isActive
                                          ? 'border-primary bg-primary/5'
                                          : 'card-border bg-gray-50/30 dark:bg-zinc-900/30'
                                      )}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-heading">{emotion.label}</p>
                                          <p className="mt-1 text-xs text-subtle">{emotion.description}</p>
                                        </div>
                                        {isActive && (
                                          <Badge className="text-[10px] uppercase tracking-wide">Selected</Badge>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            )}

                            {voiceEditorPanel === 'speed' && (
                            <div className="space-y-3 rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 p-4">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <Label className="text-sm font-semibold">Speed</Label>
                                  <p className="text-xs text-subtle">Tune cadence from measured to energetic.</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-heading">{selectedSpeed.toFixed(2)}</p>
                                  <p className="text-xs text-subtle">{describeVoiceSpeed(selectedSpeed)}</p>
                                </div>
                              </div>
                              <Slider
                                value={[selectedSpeed]}
                                min={0.6}
                                max={1.5}
                                step={0.05}
                                onValueChange={(value) => updateVoiceSettings({
                                  voice_id: formData.voice_settings?.voice_id || '',
                                  speed: Number(value[0].toFixed(2)),
                                })}
                              />
                              <div className="flex justify-between text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                                <span>Measured</span>
                                <span>Natural</span>
                                <span>Energetic</span>
                              </div>
                            </div>
                            )}

                            <div className="overflow-hidden rounded-lg border border-border/80 bg-background/60">
                              <div className="border-b border-border/70 bg-gray-50/40 px-4 py-4 dark:bg-zinc-900/30 md:px-5">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div className="space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <ConfigFieldLabel
                                        label="Pronunciation Library"
                                        description="Organization-level pronunciation overrides applied to this agent."
                                      />
                                      <Badge variant={pronunciationLibraryDirty ? 'default' : 'secondary'} className="text-[10px] uppercase tracking-wide">
                                        {pronunciationLibraryDirty ? 'Unsaved' : 'Saved'}
                                      </Badge>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="gap-1.5"
                                      onClick={addPronunciationEntry}
                                    >
                                      <Plus className="h-4 w-4" />
                                      Add Entry
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="gap-1.5"
                                      onClick={savePronunciationLibrary}
                                      disabled={
                                        savePronunciationLibraryMutation.isPending
                                        || isPronunciationLibraryLoading
                                        || !pronunciationLibraryDirty
                                      }
                                    >
                                      {savePronunciationLibraryMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Save className="h-4 w-4" />
                                      )}
                                      {pronunciationLibraryDirty ? 'Save Changes' : 'Saved'}
                                    </Button>
                                  </div>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                                  <span className="rounded-md border border-border/70 bg-background px-2 py-1 text-subtle">
                                    IPA example: <span className="font-mono text-foreground">&lt;&lt;həˈbəʊz&gt;&gt;</span>
                                  </span>
                                  <span className="rounded-md border border-border/70 bg-background px-2 py-1 text-subtle">
                                    Sounds-like example: <span className="font-mono text-foreground">hohbz</span>
                                  </span>
                                </div>
                                {hasIncompletePronunciationRow && (
                                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                    Complete both fields for each row before saving.
                                  </p>
                                )}
                              </div>

                              <div className="space-y-4 px-4 py-4 md:px-5">
                                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                  <div className="relative w-full md:max-w-md">
                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                      value={pronunciationSearch}
                                      onChange={(e) => setPronunciationSearch(e.target.value)}
                                      placeholder="Search words or pronunciations"
                                      className="h-10 pl-9"
                                    />
                                  </div>
                                  <p className="text-xs text-subtle">
                                    {filteredPronunciationRows.length} of {pronunciationEntries.length} entries · Max {MAX_PRONUNCIATION_ENTRIES}
                                  </p>
                                </div>

                                {isPronunciationLibraryLoading ? (
                                  <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-background/40 py-10 text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                  </div>
                                ) : filteredPronunciationRows.length === 0 ? (
                                  <div className="rounded-lg border border-dashed border-border bg-background/40 p-6 text-center">
                                    <p className="text-sm font-medium text-heading">
                                      {pronunciationEntries.length === 0 ? 'No pronunciation entries yet' : 'No entries match this search'}
                                    </p>
                                    <p className="mt-1 text-xs text-subtle">
                                      {pronunciationEntries.length === 0
                                        ? 'Add your first entry to enforce consistent pronunciation across all agents.'
                                        : 'Try a different search term.'}
                                    </p>
                                    {pronunciationEntries.length === 0 && (
                                      <Button type="button" variant="outline" size="sm" className="mt-4 gap-1.5" onClick={addPronunciationEntry}>
                                        <Plus className="h-4 w-4" />
                                        Add First Entry
                                      </Button>
                                    )}
                                  </div>
                                ) : (
                                  <div className="overflow-hidden rounded-lg border border-border/70">
                                    <div className="hidden grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_48px] border-b border-border/70 bg-gray-50/60 px-3 py-2 text-xs font-medium text-subtle dark:bg-zinc-900/50 md:grid">
                                      <span>#</span>
                                      <span>Word or phrase</span>
                                      <span>Pronunciation (IPA or sounds-like)</span>
                                      <span />
                                    </div>
                                    <div className="divide-y divide-border/60">
                                      {filteredPronunciationRows.map(({ entry, index }) => (
                                        <div
                                          key={`pronunciation-row-${index}`}
                                          className="grid grid-cols-1 gap-3 px-3 py-3 md:grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_48px] md:items-center"
                                        >
                                          <div className="hidden h-8 w-8 items-center justify-center rounded-md border border-border/70 text-xs text-subtle md:flex">
                                            {index + 1}
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-[11px] text-subtle md:hidden">Word or phrase</Label>
                                            <Input
                                              value={entry.text}
                                              onChange={(e) => updatePronunciationEntry(index, 'text', e.target.value)}
                                              placeholder="Word or phrase"
                                              className="h-10"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-[11px] text-subtle md:hidden">Pronunciation</Label>
                                            <Input
                                              value={entry.alias}
                                              onChange={(e) => updatePronunciationEntry(index, 'alias', e.target.value)}
                                              placeholder="Pronunciation (IPA or sounds-like)"
                                              className="h-10"
                                            />
                                          </div>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removePronunciationEntry(index)}
                                            aria-label="Remove entry"
                                            className="justify-self-end"
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="collateralAccess" className="mt-0 space-y-4">
                        <ConfigSurface>
                          <ConfigRow
                            htmlFor="use_active_deck"
                            label="Active Deck"
                            description="Enable the agent to reference slide content."
                            action={(
                            <Switch
                              id="use_active_deck"
                              checked={formData.use_active_deck !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_active_deck: checked })}
                            />
                            )}
                          >
                            <span className="text-muted-foreground">{formData.use_active_deck !== false ? 'Available during sessions.' : 'Excluded from sessions.'}</span>
                          </ConfigRow>

                          <ConfigRow
                            htmlFor="use_all_videos"
                            label="Videos"
                            description="Enable the agent to search video content."
                            action={(
                            <Switch
                              id="use_all_videos"
                              checked={formData.use_all_videos !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_all_videos: checked })}
                            />
                            )}
                          >
                            <span className="text-muted-foreground">{formData.use_all_videos !== false ? 'Available during sessions.' : 'Excluded from sessions.'}</span>
                          </ConfigRow>

                          <ConfigRow
                            htmlFor="use_documentation"
                            label="Documentation"
                            description="Enable the agent to show documentation during sessions."
                            action={(
                            <Switch
                              id="use_documentation"
                              checked={formData.use_documentation === true}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_documentation: checked })}
                            />
                            )}
                          >
                            <span className="text-muted-foreground">{formData.use_documentation === true ? 'Available during sessions.' : 'Excluded from sessions.'}</span>
                          </ConfigRow>

                        </ConfigSurface>
                      </TabsContent>

                      {activeEditSection === 'conversation' && (
                        <ConfigSurface>
                          <ConfigRow
                            htmlFor="conversation_control_mode"
                            label="Conversation Approach"
                            description="Choose how assertively the agent should guide the visitor through the demo."
                          >
                              <Select
                                value={formData.conversation_control_mode || 'balanced'}
                                onValueChange={(value) => setFormData({ ...formData, conversation_control_mode: value as SalesAgent['conversation_control_mode'] })}
                              >
                                <SelectTrigger id="conversation_control_mode" className="h-9 rounded-lg">
                                  <SelectValue placeholder="Select conversation style" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CONVERSATION_CONTROL_MODE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                          </ConfigRow>

                          <ConfigRow
                            htmlFor="auto_suggestions_enabled"
                            label="Suggested Follow-ups"
                            description="Show follow-up suggestion pills to visitors during live sessions."
                            action={(
                            <Switch
                              id="auto_suggestions_enabled"
                              checked={formData.auto_suggestions_enabled !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, auto_suggestions_enabled: checked })}
                            />
                            )}
                          >
                            <span className="text-muted-foreground">
                              {formData.auto_suggestions_enabled !== false ? 'Enabled for live sessions.' : 'Hidden during live sessions.'}
                            </span>
                          </ConfigRow>

                          {formData.auto_suggestions_enabled !== false && (
                          <ConfigRow
                            htmlFor="suggestion_instructions"
                            label="Follow-up Guidance"
                            description="Shape how follow-up suggestion chips guide the conversation after each turn."
                            className="md:items-start"
                          >
                              <Textarea
                                id="suggestion_instructions"
                                value={formData.suggestion_instructions || ''}
                                onChange={(e) => setFormData({ ...formData, suggestion_instructions: e.target.value })}
                                placeholder="Example: Keep one suggestion focused on ROI, one on implementation, and avoid pricing unless the visitor asks."
                                className="min-h-[140px] rounded-lg"
                                maxLength={2000}
                              />
                          </ConfigRow>
                          )}
                        </ConfigSurface>
                      )}

                      {activeEditSection === 'branding' && (
                        <div className="grid w-full max-w-none gap-4 lg:grid-cols-[minmax(440px,0.38fr)_minmax(0,1.62fr)] lg:items-start">
                          <div className="space-y-4">
                        <ConfigGroup title="Brand Assets">
                          <ConfigSurface className="max-w-none">
                          <ConfigRow label="Entry Wordmark" description="Brand mark shown in the agent entry prompt." className={cn(BRANDING_ROW_CLASS, 'md:items-start')}>
                            <div className="space-y-3">
                              {formData.entry_modal_logo_url ? (
                                <div className="relative inline-flex rounded-lg border border-border/70 p-1" style={TRANSPARENCY_GRID_STYLE}>
                                  <img
                                    src={formData.entry_modal_logo_url}
                                    alt="Entry wordmark"
                                    className="h-12 w-auto rounded-lg object-contain"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                    onClick={() => setFormData({ ...formData, entry_modal_logo_url: '' })}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex h-12 w-24 items-center justify-center rounded-lg border border-dashed border-border bg-background">
                                  <Image className="h-6 w-6 text-muted-foreground/50" />
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-logo-url.com/logo.png"
                                    value={formData.entry_modal_logo_url || ''}
                                    onChange={(e) => setFormData({ ...formData, entry_modal_logo_url: e.target.value })}
                                    className="h-9 min-w-[240px] flex-1 rounded-lg"
                                  />
                                  <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'entry_modal_logo_url', 'agent_logo', setIsUploadingLogo);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingLogo}
                                    onClick={() => logoInputRef.current?.click()}
                                    className="h-9 gap-1.5 rounded-lg"
                                  >
                                    {isUploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingLogo ? 'Uploading...' : 'Upload'}
                                  </Button>
                              </div>
                            </div>
                          </ConfigRow>

                          <ConfigRow label="Active Wordmark" description="Light brand mark shown in the active agent header. Falls back to the entry wordmark when empty." className={cn(BRANDING_ROW_CLASS, 'md:items-start')}>
                            <div className="space-y-3">
                              {formData.header_logo_url ? (
                                <div className="relative inline-flex rounded-lg border border-border/70 bg-zinc-900 p-1">
                                  <img
                                    src={formData.header_logo_url}
                                    alt="Active wordmark"
                                    className="h-12 w-auto rounded-lg object-contain"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                    onClick={() => setFormData({ ...formData, header_logo_url: '' })}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex h-12 w-24 items-center justify-center rounded-lg border border-dashed border-border bg-zinc-900/50">
                                  <Image className="h-6 w-6 text-muted-foreground/50" />
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-logo-url.com/light-wordmark.png"
                                    value={formData.header_logo_url || ''}
                                    onChange={(e) => setFormData({ ...formData, header_logo_url: e.target.value })}
                                    className="h-9 min-w-[240px] flex-1 rounded-lg"
                                  />
                                  <input
                                    ref={headerLogoInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'header_logo_url', 'agent_logo', setIsUploadingHeaderLogo);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingHeaderLogo}
                                    onClick={() => headerLogoInputRef.current?.click()}
                                    className="h-9 gap-1.5 rounded-lg"
                                  >
                                    {isUploadingHeaderLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingHeaderLogo ? 'Uploading...' : 'Upload'}
                                  </Button>
                              </div>
                            </div>
                          </ConfigRow>
                          <ConfigRow label="Icon" description="Square icon displayed inside the Agent Button." className={cn(BRANDING_ROW_CLASS, 'md:items-start')}>
                            <div className="space-y-3">
                              {formData.sticky_trigger_icon_url ? (
                                <div className="relative inline-flex rounded-full border border-border/70 p-1" style={TRANSPARENCY_GRID_STYLE}>
                                  <img
                                    src={formData.sticky_trigger_icon_url}
                                    alt="Agent icon"
                                    className="h-10 w-10 rounded-full object-cover"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                    onClick={() => setFormData({ ...formData, sticky_trigger_icon_url: '' })}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-dashed border-border bg-background">
                                  <Image className="h-5 w-5 text-muted-foreground/50" />
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2">
                                <Input
                                  type="url"
                                  placeholder="https://your-icon-url.com/icon.png"
                                  value={formData.sticky_trigger_icon_url || ''}
                                  onChange={(e) => setFormData({ ...formData, sticky_trigger_icon_url: e.target.value })}
                                  className="h-9 min-w-[240px] flex-1 rounded-lg"
                                />
                                <input
                                  ref={launcherIconInputRef}
                                  type="file"
                                  accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(file, 'sticky_trigger_icon_url', 'agent_icon', setIsUploadingLauncherIcon);
                                    e.target.value = '';
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={isUploadingLauncherIcon}
                                  onClick={() => launcherIconInputRef.current?.click()}
                                  className="h-9 gap-1.5 rounded-lg"
                                >
                                  {isUploadingLauncherIcon ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                  {isUploadingLauncherIcon ? 'Uploading...' : 'Upload'}
                                </Button>
                              </div>
                            </div>
                          </ConfigRow>
                          </ConfigSurface>
                        </ConfigGroup>

                        <ConfigGroup title="Agent Button">
                          <ConfigSurface className="max-w-none">

                          <ConfigRow htmlFor="sticky_trigger_text" label="Button Text" description="Short call-to-action shown in the Agent Button." className={BRANDING_ROW_CLASS}>
                            <Input
                              id="sticky_trigger_text"
                              value={formData.sticky_trigger_text || ''}
                              onChange={(e) => setFormData({ ...formData, sticky_trigger_text: e.target.value })}
                              placeholder="Start Demo"
                              className="h-9 rounded-lg"
                            />
                          </ConfigRow>

                          <ConfigRow label="Font Family" description="Typeface used for the Agent Button text." className={BRANDING_ROW_CLASS}>
                            <Select
                              value={formData.sticky_trigger_font_family || '__default__'}
                              onValueChange={(val) => setFormData({ ...formData, sticky_trigger_font_family: val === '__default__' ? null : val })}
                            >
                              <SelectTrigger className="h-9 rounded-lg">
                                <SelectValue placeholder="Default (system font)" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                <SelectItem value="__default__">Default (system font)</SelectItem>
                                {googleFonts.map((font) => (
                                  <SelectItem key={font.value} value={font.value}>
                                    {font.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </ConfigRow>

                          <ConfigRow label="Tint" description="Background color used for the Agent Button." className={BRANDING_ROW_CLASS}>
                            <LauncherTintPicker
                              value={formData.sticky_trigger_bg_color}
                              onChange={(value) => setFormData({ ...formData, sticky_trigger_bg_color: value })}
                            />
                          </ConfigRow>

                          <ConfigRow label="Icon Background" description="Color of the small circle behind the Agent Button icon." className={BRANDING_ROW_CLASS}>
                            <LauncherTintPicker
                              value={formData.launcher_icon_bg_color}
                              onChange={(value) => setFormData({ ...formData, launcher_icon_bg_color: value })}
                            />
                          </ConfigRow>

                          <ConfigRow label="Hover Thumbnail" description="Optional 16:9 image shown when visitors hover the Agent Button." className={cn(BRANDING_ROW_CLASS, 'md:items-start')}>
                            <div className="space-y-3">
                              <ConfigPreviewSlot className="min-h-[170px]">
                                {formData.sticky_trigger_thumbnail_url ? (
                                  <div className="relative w-full max-w-sm overflow-hidden rounded-lg border border-border/70" style={TRANSPARENCY_GRID_STYLE}>
                                    <div className="aspect-video w-full">
                                      <img
                                        src={formData.sticky_trigger_thumbnail_url}
                                        alt="Agent Button hover thumbnail"
                                        className="h-full w-full object-cover"
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                      onClick={() => setFormData({ ...formData, sticky_trigger_thumbnail_url: '' })}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <ConfigEmptyState icon={<Image className="mx-auto h-10 w-10 text-muted-foreground/50" />} title="No hover thumbnail" />
                                )}
                              </ConfigPreviewSlot>
                              <div className="flex flex-wrap gap-2">
                                <Input
                                  type="url"
                                  placeholder="https://your-thumbnail-url.com/image.jpg"
                                  value={formData.sticky_trigger_thumbnail_url || ''}
                                  onChange={(e) => setFormData({ ...formData, sticky_trigger_thumbnail_url: e.target.value })}
                                  className="h-9 min-w-[240px] flex-1 rounded-lg"
                                />
                                <input
                                  ref={launcherThumbnailInputRef}
                                  type="file"
                                  accept=".png,.jpg,.jpeg,.webp,.gif"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(file, 'sticky_trigger_thumbnail_url', 'launcher_thumbnail', setIsUploadingLauncherThumbnail);
                                    e.target.value = '';
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={isUploadingLauncherThumbnail}
                                  onClick={() => launcherThumbnailInputRef.current?.click()}
                                  className="h-9 gap-1.5 rounded-lg"
                                >
                                  {isUploadingLauncherThumbnail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                  {isUploadingLauncherThumbnail ? 'Uploading...' : 'Upload'}
                                </Button>
                              </div>
                            </div>
                          </ConfigRow>

                          </ConfigSurface>
                        </ConfigGroup>

                        <ConfigGroup title="Link Previews">
                          <ConfigSurface className="max-w-none">
                            <ConfigRow label="Default Link Title" description="Title shown when the main demo link is shared." className={BRANDING_ROW_CLASS}>
                              <Input
                                value={formData.share_title || ''}
                                onChange={(e) => setFormData({ ...formData, share_title: e.target.value })}
                                placeholder={`${formData.company_name || formData.name || 'Company'} Demo`}
                                className="h-9 rounded-lg"
                              />
                            </ConfigRow>
                            <ConfigRow label="Default Link Description" description="Description shown in email and social previews." className={cn(BRANDING_ROW_CLASS, 'md:items-start')}>
                              <Textarea
                                rows={3}
                                value={formData.share_description || ''}
                                onChange={(e) => setFormData({ ...formData, share_description: e.target.value })}
                                placeholder={`Explore an interactive product demo from ${formData.company_name || formData.name || 'this company'}.`}
                                className="min-h-[86px] rounded-lg"
                              />
                            </ConfigRow>
                            <ConfigRow label="Default Link Thumbnail" description="16:9 image shown when the main demo link is shared." className={cn(BRANDING_ROW_CLASS, 'md:items-start')}>
                              <div className="space-y-3">
                                <ConfigPreviewSlot className="min-h-[170px]">
                                  {formData.share_thumbnail_url ? (
                                    <div className="relative w-full max-w-sm overflow-hidden rounded-lg border border-border/70" style={TRANSPARENCY_GRID_STYLE}>
                                      <div className="aspect-video w-full">
                                        <img
                                          src={formData.share_thumbnail_url}
                                          alt="Default link thumbnail"
                                          className="h-full w-full object-cover"
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                        onClick={() => setFormData({ ...formData, share_thumbnail_url: '' })}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <ConfigEmptyState icon={<Image className="mx-auto h-10 w-10 text-muted-foreground/50" />} title="Uses demo image fallback" />
                                  )}
                                </ConfigPreviewSlot>
                                <div className="flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-thumbnail-url.com/link-preview.jpg"
                                    value={formData.share_thumbnail_url || ''}
                                    onChange={(e) => setFormData({ ...formData, share_thumbnail_url: e.target.value })}
                                    className="h-9 min-w-[240px] flex-1 rounded-lg"
                                  />
                                  <input
                                    ref={shareThumbnailInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'share_thumbnail_url', 'share_thumbnail', setIsUploadingShareThumbnail);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingShareThumbnail}
                                    onClick={() => shareThumbnailInputRef.current?.click()}
                                    className="h-9 gap-1.5 rounded-lg"
                                  >
                                    {isUploadingShareThumbnail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingShareThumbnail ? 'Uploading...' : 'Upload'}
                                  </Button>
                                </div>
                              </div>
                            </ConfigRow>
                          </ConfigSurface>
                        </ConfigGroup>

                        <ConfigGroup title="Start Screen">
                          <ConfigSurface className="max-w-none">
                          <ConfigRow label="Start Screen" description="Image shown before the first piece of demo content loads." className={cn(BRANDING_ROW_CLASS, 'md:items-start')}>
                            <div className="space-y-3">
                              <ConfigPreviewSlot className="min-h-[260px]">
                                {formData.background_screenshot_url ? (
                                  <div
                                    className="relative flex w-full items-center justify-center rounded-lg"
                                    style={TRANSPARENCY_GRID_STYLE}
                                  >
                                    <img
                                      src={formData.background_screenshot_url}
                                      alt="Start screen preview"
                                      className="max-h-[220px] w-auto max-w-full rounded-lg object-contain"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                      onClick={() => setFormData({ ...formData, background_screenshot_url: '' })}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <ConfigEmptyState icon={<Image className="mx-auto h-10 w-10 text-muted-foreground/50" />} title="No start screen selected" />
                                )}
                              </ConfigPreviewSlot>
                              <div className="flex flex-wrap gap-2">
                                <Input
                                  type="url"
                                  placeholder="https://example.com/screenshot.png"
                                  value={formData.background_screenshot_url || ''}
                                  onChange={(e) => setFormData({ ...formData, background_screenshot_url: e.target.value })}
                                  className="h-9 min-w-[240px] flex-1 rounded-lg"
                                />
                                <input
                                  ref={backgroundInputRef}
                                  type="file"
                                  accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(file, 'background_screenshot_url', 'background_screenshot', setIsUploadingBackground);
                                    e.target.value = '';
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={isUploadingBackground}
                                  onClick={() => backgroundInputRef.current?.click()}
                                  className="h-9 gap-1.5 rounded-lg"
                                >
                                  {isUploadingBackground ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                  {isUploadingBackground ? 'Uploading...' : 'Upload Image'}
                                </Button>
                              </div>
                            </div>
                          </ConfigRow>
                          </ConfigSurface>
                        </ConfigGroup>
                          </div>

                          <div className="lg:sticky lg:top-20">
                            <ExperiencePreviewPanel
                              iconUrl={formData.sticky_trigger_icon_url || ''}
                              text={formData.sticky_trigger_text || ''}
                              tintColor={formData.sticky_trigger_bg_color || null}
                              textColor={formData.sticky_trigger_text_color || null}
                              fontFamily={formData.sticky_trigger_font_family || null}
                              thumbnailUrl={formData.sticky_trigger_thumbnail_url || ''}
                              leadCaptureEmail={formData.lead_capture_email || 'required'}
                              iconBgColor={formData.launcher_icon_bg_color || null}
                              activeScreenshotUrl={formData.background_screenshot_url || null}
                              headerLogoUrl={formData.header_logo_url || formData.entry_modal_logo_url || null}
                              entryModalLogoUrl={formData.entry_modal_logo_url || null}
                              companyName={formData.company_name || formData.name || null}
                            />
                          </div>
                        </div>
                      )}

                      {activeEditSection === 'conversation' && (
                        <div className="max-w-4xl space-y-6">
                          <div className="flex items-center gap-3">
                            <Select
                              onValueChange={(country) => {
                                const bank = { ...(formData.terminology_bank || {}) };
                                if (!bank[country]) {
                                  bank[country] = [];
                                }
                                setFormData({ ...formData, terminology_bank: bank });
                              }}
                            >
                              <SelectTrigger className="h-9 w-64 rounded-lg">
                                <SelectValue placeholder="Add a region..." />
                              </SelectTrigger>
                              <SelectContent>
                                {(terminologyCountries ?? []).filter((c) => !formData.terminology_bank?.[c]).map((country) => (
                                  <SelectItem key={country} value={country}>{country}</SelectItem>
                                ))}
                                {['AU', 'GB', 'US', 'CA', 'NZ', 'IE', 'IN', 'SG', 'ZA', 'DE', 'FR', 'JP']
                                  .filter((c) => !formData.terminology_bank?.[c] && !(terminologyCountries ?? []).includes(c))
                                  .map((country) => (
                                    <SelectItem key={country} value={country}>{country}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <p className="text-sm text-muted-foreground">Select a country code to add region-specific terms.</p>
                          </div>

                          {formData.terminology_bank && Object.keys(formData.terminology_bank).length > 0 ? (
                            Object.entries(formData.terminology_bank).map(([country, entries]) => (
                              <div key={country} className="rounded-lg border border-border/80 bg-background/60">
                                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                                  <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                                    <Globe2 className="h-4 w-4 text-muted-foreground" />
                                    {country}
                                  </h3>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 gap-1 text-xs"
                                      onClick={() => {
                                        const bank = { ...(formData.terminology_bank || {}) };
                                        bank[country] = [...(bank[country] || []), { term: '', context: '' }];
                                        setFormData({ ...formData, terminology_bank: bank });
                                      }}
                                    >
                                      <Plus className="h-3 w-3" /> Add Term
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 text-xs text-destructive hover:text-destructive"
                                      onClick={() => {
                                        const bank = { ...(formData.terminology_bank || {}) };
                                        delete bank[country];
                                        setFormData({ ...formData, terminology_bank: Object.keys(bank).length > 0 ? bank : null });
                                      }}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>

                                <div className="divide-y divide-border/60">
                                  {(entries as TerminologyEntry[]).map((entry, idx) => (
                                    <div key={idx} className="flex items-start gap-3 px-5 py-3">
                                      <div className="flex-1 space-y-2">
                                        <Input
                                          placeholder="Term (e.g. organisation)"
                                          value={entry.term}
                                          onChange={(e) => {
                                            const bank = { ...(formData.terminology_bank || {}) };
                                            const list = [...(bank[country] || [])];
                                            list[idx] = { ...list[idx], term: e.target.value };
                                            bank[country] = list;
                                            setFormData({ ...formData, terminology_bank: bank });
                                          }}
                                          className="h-9 rounded-lg"
                                        />
                                        <Input
                                          placeholder="Context (optional — e.g. Use Australian English spelling)"
                                          value={entry.context || ''}
                                          onChange={(e) => {
                                            const bank = { ...(formData.terminology_bank || {}) };
                                            const list = [...(bank[country] || [])];
                                            list[idx] = { ...list[idx], context: e.target.value || undefined };
                                            bank[country] = list;
                                            setFormData({ ...formData, terminology_bank: bank });
                                          }}
                                          className="h-9 rounded-lg"
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="mt-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                                        onClick={() => {
                                          const bank = { ...(formData.terminology_bank || {}) };
                                          const list = [...(bank[country] || [])];
                                          list.splice(idx, 1);
                                          if (list.length === 0) {
                                            delete bank[country];
                                            setFormData({ ...formData, terminology_bank: Object.keys(bank).length > 0 ? bank : null });
                                          } else {
                                            bank[country] = list;
                                            setFormData({ ...formData, terminology_bank: bank });
                                          }
                                        }}
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  ))}
                                  {(entries as TerminologyEntry[]).length === 0 && (
                                    <div className="px-5 py-6 text-center text-sm text-muted-foreground">
                                      No terms yet. Click "Add Term" to get started.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16">
                              <Languages className="h-10 w-10 text-muted-foreground/40" />
                              <p className="mt-4 text-sm font-medium text-heading">No terminology configured</p>
                              <p className="mt-1 text-sm text-muted-foreground">Select a region above to start adding terms.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </form>
                )}
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default SalesAgentContent;
